import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Tv2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import MatchViewModal from '../components/MatchViewModal';
import { useKioskInactivity } from '../hooks/useKioskInactivity';

const API_URL    = process.env.REACT_APP_BACKEND_URL;

const NAT_TO_ISO = {
    'english': 'gb-eng', 'scottish': 'gb-sct', 'welsh': 'gb-wls',
    'northern irish': 'gb-nir', 'irish': 'ie',
    'norwegian': 'no', 'swedish': 'se', 'danish': 'dk', 'finnish': 'fi', 'icelandic': 'is',
    'german': 'de', 'austrian': 'at', 'swiss': 'ch', 'dutch': 'nl', 'belgian': 'be',
    'french': 'fr', 'spanish': 'es', 'portuguese': 'pt', 'italian': 'it', 'greek': 'gr',
    'turkish': 'tr', 'polish': 'pl', 'czech': 'cz', 'slovak': 'sk', 'hungarian': 'hu',
    'romanian': 'ro', 'bulgarian': 'bg', 'croatian': 'hr', 'serbian': 'rs', 'slovenian': 'si',
    'brazilian': 'br', 'argentinian': 'ar', 'uruguayan': 'uy', 'colombian': 'co',
    'american': 'us', 'canadian': 'ca', 'australian': 'au',
    'japanese': 'jp', 'south korean': 'kr', 'korean': 'kr',
    'moroccan': 'ma', 'algerian': 'dz', 'egyptian': 'eg', 'senegalese': 'sn',
    'nigerian': 'ng', 'ghanaian': 'gh', 'ivorian': 'ci', 'cameroonian': 'cm',
    'russian': 'ru', 'ukrainian': 'ua', 'georgian': 'ge',
};

function getFlagUrl(nationality) {
    if (!nationality) return null;
    const iso = NAT_TO_ISO[nationality.toLowerCase().trim()];
    return iso ? `https://flagcdn.com/24x18/${iso}.png` : null;
}

function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

// ── Shared atoms ──────────────────────────────────────────────────────────────

function TeamBadge({ teamId, teamName, size = 'md' }) {
    const [err, setErr] = useState(false);
    const sizes = { sm: 'w-6 h-6', md: 'w-10 h-10', lg: 'w-16 h-16', xl: 'w-20 h-20' };
    const texts = { sm: 'text-xs', md: 'text-sm', lg: 'text-2xl', xl: 'text-3xl' };
    const dim = sizes[size] || sizes.md;
    const txt = texts[size] || texts.md;
    if (err || !teamId) {
        return (
            <div className={`${dim} rounded-full bg-white/10 flex items-center justify-center flex-shrink-0`}>
                <span className={`${txt} font-bold text-white/60`}>{teamName?.charAt(0) || '?'}</span>
            </div>
        );
    }
    return (
        <img src={badgeUrl(teamId)} alt={teamName}
            className={`${dim} object-contain flex-shrink-0`} onError={() => setErr(true)} />
    );
}

function TeamBadgeLight({ teamId, teamName, size = 'sm' }) {
    const [err, setErr] = useState(false);
    const dim = size === 'lg' ? 'w-12 h-12' : size === 'md' ? 'w-7 h-7' : 'w-5 h-5';
    if (err || !teamId) {
        return (
            <div className={`${dim} rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
                <span className="text-[10px] font-bold text-muted-foreground">{teamName?.charAt(0) || '?'}</span>
            </div>
        );
    }
    return (
        <img src={badgeUrl(teamId)} alt={teamName}
            className={`${dim} object-contain flex-shrink-0`} onError={() => setErr(true)} />
    );
}

function PlayerCircle({ playerId, playerName, shirt, size = 'md', teamId }) {
    const [err, setErr] = useState(false);
    const { pathname } = useLocation();
    const isWcRoute = pathname.startsWith('/wc2026/');
    const useWcImg  = (isWcRoute || isWcTeam(teamId)) && !err;

    const dim     = size === 'xl' ? 'w-20 h-20' : size === 'lg' ? 'w-14 h-14' : 'w-11 h-11';
    const txtSize = size === 'xl' ? 'text-xl'   : size === 'lg' ? 'text-base'  : 'text-[13px]';

    if (playerId && !err) {
        if (useWcImg) {
            // WC full-body images: zoom in on the head/shoulders area using
            // background-image so we can control crop position precisely.
            // background-size: auto 380% → the image is rendered at 3.8× the
            // circle height; background-position: 50% 3% shows the very top
            // (head + shoulders) centred horizontally.
            const src = wcPlayerUrl(playerId);
            return (
                <div
                    className={`${dim} rounded-full border-2 border-white/50 flex-shrink-0 bg-white/10`}
                    style={{
                        backgroundImage: `url(${src})`,
                        backgroundSize: 'auto 230%',
                        backgroundPosition: '50% 5%',
                        backgroundRepeat: 'no-repeat',
                    }}
                    role="img"
                    aria-label={playerName}
                >
                    {/* hidden img so onError fires if the AVIF fails to load */}
                    <img
                        src={src}
                        alt=""
                        className="sr-only"
                        onError={() => setErr(true)}
                    />
                </div>
            );
        }
        return (
            <img src={playerUrl(playerId)} alt={playerName}
                className={`${dim} rounded-full object-cover object-top border-2 border-white/50 flex-shrink-0`}
                onError={() => setErr(true)} />
        );
    }
    return (
        <div className={`${dim} rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center flex-shrink-0`}>
            <span className={`${txtSize} font-bold text-white leading-none`}>{shirt || '?'}</span>
        </div>
    );
}

// ── Match header ──────────────────────────────────────────────────────────────

function MatchHeader({ m, liveStatus, navigate, isKioskRoute, matchId }) {
    // Hooks must be called unconditionally — before any early returns
    const [refereeName, setRefereeName] = useState(null);
    useEffect(() => {
        if (!matchId) return;
        axios.get(`${API_URL}/api/football/match/${matchId}/referee`, { headers: authHeaders() })
            .then(r => { if (r.data?.available) setRefereeName(r.data.referee?.name || null); })
            .catch(() => {});
    }, [matchId]);

    if (!m) return null;
    // Prefer live-status scores (updated every 15 s) over the DB match doc
    const homeScore  = liveStatus?.home_score ?? m.home_score;
    const awayScore  = liveStatus?.away_score ?? m.away_score;
    const hasScore   = homeScore != null && awayScore != null;

    // m.is_result (from PA match/events) is the primary finished signal for all competitions.
    const _isResult = m?.is_result === true;
    const _FINISHED_PHASES = ['full_time', 'after_et', 'abandoned', 'postponed'];
    const _IN_PROGRESS_PHASES = ['first_half', 'second_half', 'half_time', 'et_first_half', 'et_half_time', 'et_second_half', 'penalties'];
    const _lsKnown = liveStatus && liveStatus.phase !== 'unknown';
    const _lsExplicitlyLive    = liveStatus && liveStatus.is_live === true;
    const _lsExplicitlyNotLive = liveStatus && liveStatus.is_live === false;
    const _dbSaysLive = !!(m?.status && m.status.toLowerCase().includes('live'));
    // PA matchMinutes is set during the match (including HT). If present the match is in progress.
    // This is the most reliable signal for competitions where match/info has no coverage.
    const _hasLiveMinute = m?.live_minute != null;
    const _lsInProgress  = _lsKnown && _IN_PROGRESS_PHASES.includes(liveStatus.phase);

    // When liveStatus has no coverage (phase='unknown'), fall back to the phase/phase_label
    // that get_hub_match_detail extracts directly from the PA match/events feed.
    // This makes Half Time, 2nd Half etc. display correctly for competitions (e.g. internationals)
    // where match/info has no coverage.
    const _mPhase       = (!_lsKnown && m?.phase)       ? m.phase       : '';
    const _mPhaseLabel  = (!_lsKnown && m?.phase_label) ? m.phase_label : '';
    // The effective phase for colour + display fallback logic
    const resolvedPhase   = (_lsKnown ? (liveStatus.phase || '') : _mPhase);
    const resolvedDisplay = (_lsKnown ? (liveStatus.display || '') : _mPhaseLabel);

    // Paused phases: clocks have stopped — match is NOT actively live (no pulse dot)
    const _PAUSED_PHASE_SLUGS = new Set(['half_time', 'et_half_time']);
    const _ACTIVE_PHASE_SLUGS = new Set(['first_half', 'second_half', 'et_first_half', 'et_second_half', 'penalties']);
    const _DONE_PHASE_SLUGS   = new Set(['full_time', 'after_et', 'abandoned', 'postponed']);
    const _isPaused   = _PAUSED_PHASE_SLUGS.has(resolvedPhase);
    const _isDone     = _DONE_PHASE_SLUGS.has(resolvedPhase);
    const _isActive   = _ACTIVE_PHASE_SLUGS.has(resolvedPhase);

    // A match is definitively over when PA confirms is_result OR liveStatus says finished phase.
    // These take priority over live_minute still being set in DB (PA can send matchMinutes=131
    // and is_result=true simultaneously at the end of extra time / penalties).
    const _definitelyOver = (
        (_isResult && !_lsExplicitlyLive && !_lsInProgress)
        || (_lsKnown && _FINISHED_PHASES.includes(liveStatus.phase) && !_lsInProgress)
        || (_lsExplicitlyNotLive && _dbSaysLive && !_lsInProgress)
        || (_isDone && !_lsExplicitlyLive)
    );
    const isFinished = _definitelyOver;
    const isLive = !isFinished && (
        _isActive
        || _lsInProgress
        || (_isPaused ? false               // half-time breaks are not "live"
            : _lsKnown ? liveStatus.is_live === true
            : (_hasLiveMinute || _dbSaysLive))
    );

    // Phase badge colours — use resolved phase so e.g. half_time shows amber even when
    // liveStatus has no coverage (resolvedPhase comes from m.phase in that case).
    const phaseColour = {
        half_time:    'bg-amber-500/20 text-amber-300 border-amber-500/30',
        et_half_time: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        full_time:    'bg-white/10 text-slate-300 border-white/20',
        after_et:     'bg-white/10 text-slate-300 border-white/20',
        penalties:    'bg-purple-500/20 text-purple-300 border-purple-500/30',
    }[resolvedPhase] || 'bg-green-500/20 text-green-400 border-green-500/30';

    const goToTeam = (teamId) => {
        if (teamId) navigate(isKioskRoute ? `/wc2026/team/${teamId}` : `/football-hub/team/${teamId}`);
    };

    return (
        <div className="rounded-2xl overflow-hidden border border-white/5 shadow-xl">
            {/* Dark gradient hero */}
            <div className="bg-gradient-to-br from-[#0a0a1a] via-[#000028] to-[#0d0d1d] px-6 pt-5 pb-6">
                {/* Competition + round */}
                {(m.competition || m.round) && (
                    <p className="text-center text-[11px] text-slate-400 uppercase tracking-widest mb-4 font-medium">
                        {[
                            (m.competition || '').trim(),
                            m.round
                        ].filter(Boolean).join(' · ')}
                    </p>
                )}

                {/* Teams + score */}
                <div className="flex items-center gap-2">
                    {/* Home */}
                    <button
                        onClick={() => goToTeam(m.home_team_id)}
                        className="flex-1 flex flex-col items-center gap-2 min-w-0 group"
                    >
                        <div className="w-16 h-16 rounded-full bg-white/8 ring-1 ring-white/10 flex items-center justify-center p-1.5 group-hover:ring-white/30 transition-all">
                            <TeamBadge teamId={m.home_team_id} teamName={m.home_team_name} size="lg" />
                        </div>
                        <span className="text-white font-semibold text-sm text-center leading-tight px-1 truncate w-full group-hover:text-primary transition-colors">
                            {m.home_team_name}
                        </span>
                    </button>

                    {/* Score block */}
                    <div className="flex flex-col items-center gap-2 flex-shrink-0 px-2">
                        {hasScore ? (
                            <div className="flex items-center gap-3">
                                <span className={`text-5xl font-black tabular-nums leading-none ${isLive && !_isPaused ? 'text-green-400' : 'text-white'}`}>
                                    {homeScore}
                                </span>
                                <span className="text-2xl text-slate-600 font-light">–</span>
                                <span className={`text-5xl font-black tabular-nums leading-none ${isLive && !_isPaused ? 'text-green-400' : 'text-white'}`}>
                                    {awayScore}
                                </span>
                            </div>
                        ) : (
                            <span className="text-3xl font-light text-slate-500">vs</span>
                        )}

                        {/* Live / phase badge — only when NOT finished */}
                        {!isFinished && (isLive || resolvedDisplay || _hasLiveMinute) && (
                            <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${phaseColour}`}>
                                {/* Pulse dot: only during active play, not half-time breaks */}
                                {(isLive && !_isPaused) && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse inline-block" />
                                )}
                                {resolvedDisplay
                                    || (liveStatus?.minute ? `${liveStatus.minute}'` : null)
                                    || (_isPaused ? null : (m?.live_minute != null ? `${m.live_minute}'` : null))
                                    || 'LIVE'}
                            </span>
                        )}
                        {/* Finished badge — shown whenever isFinished, regardless of live_minute in DB */}
                        {isFinished && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="bg-white/10 text-slate-300 text-xs font-semibold px-3 py-1 rounded-full">
                                    {liveStatus?.display || 'Full Time'}
                                </span>
                                {m.attendance && (
                                    <span className="text-slate-500 text-[11px]">
                                        {Number(m.attendance).toLocaleString()} att.
                                    </span>
                                )}
                            </div>
                        )}
                        {!isFinished && !isLive && m.kickoff_time && (
                            <span className="text-slate-400 text-sm font-medium">
                                {format(parseISO(m.kickoff_time), 'HH:mm')}
                            </span>
                        )}
                    </div>

                    {/* Away */}
                    <button
                        onClick={() => goToTeam(m.away_team_id)}
                        className="flex-1 flex flex-col items-center gap-2 min-w-0 group"
                    >
                        <div className="w-16 h-16 rounded-full bg-white/8 ring-1 ring-white/10 flex items-center justify-center p-1.5 group-hover:ring-white/30 transition-all">
                            <TeamBadge teamId={m.away_team_id} teamName={m.away_team_name} size="lg" />
                        </div>
                        <span className="text-white font-semibold text-sm text-center leading-tight px-1 truncate w-full group-hover:text-primary transition-colors">
                            {m.away_team_name}
                        </span>
                    </button>
                </div>
            </div>

            {/* Metadata strip */}
            {(m.kickoff_time || m.venue || refereeName) && (
                <div className="bg-[#06060f] border-t border-white/5 px-4 py-2 flex items-center justify-center gap-4 flex-wrap">
                    {m.kickoff_time && (
                        <span className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Calendar className="h-3 w-3" />
                            {format(parseISO(m.kickoff_time), 'EEE d MMM yyyy, HH:mm')}
                        </span>
                    )}
                    {m.venue && (
                        <span className="flex items-center gap-1.5 text-xs text-slate-500">
                            <MapPin className="h-3 w-3" />
                            {m.venue}
                        </span>
                    )}
                    {refereeName && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                            <span className="text-slate-600">🟨</span>
                            <span>Ref: <span className="text-slate-400">{refereeName}</span></span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Summary / timeline ────────────────────────────────────────────────────────

function eventStyle(type) {
    switch (type) {
        case 'goal':           return { bg: 'bg-green-500',   border: 'ring-green-400/50',  icon: '⚽', label: 'Goal' };
        case 'penalty goal':   return { bg: 'bg-green-600',   border: 'ring-green-500/50',  icon: '⚽', label: 'Penalty' };
        case 'own goal':       return { bg: 'bg-orange-500',  border: 'ring-orange-400/50', icon: '⚽', label: 'OG' };
        case 'yellow card':    return { bg: 'bg-yellow-400',  border: 'ring-yellow-300/50', icon: '🟨', label: 'Yellow' };
        case 'red card':       return { bg: 'bg-red-500',     border: 'ring-red-400/50',    icon: '🟥', label: 'Red' };
        case 'yellow/red card':return { bg: 'bg-red-500',     border: 'ring-red-400/50',    icon: '🟥', label: '2nd Yellow' };
        case 'substitution':   return { bg: 'bg-slate-500',   border: 'ring-slate-400/40',  icon: '↔', label: 'Sub' };
        case 'penalty miss':   return { bg: 'bg-slate-400',   border: 'ring-slate-300/40',  icon: '✕', label: 'Pen Miss' };
        case 'assist':         return { bg: 'bg-blue-500',    border: 'ring-blue-400/50',   icon: '🅰', label: 'Assist' };
        default:               return { bg: 'bg-slate-500',   border: 'ring-slate-400/40',  icon: '•', label: '' };
    }
}

function EventPlayerPhoto({ playerId, playerName, teamId }) {
    const [err, setErr] = useState(false);
    const { pathname } = useLocation();
    const isWcRoute = pathname.startsWith('/wc2026/');
    const useWcImg = (isWcRoute || isWcTeam(teamId)) && !err;

    if (playerId && playerId !== '0') {
        if (useWcImg) {
            // WC full-body image: use background-image so we can zoom into head+shoulders
            const src = wcPlayerUrl(playerId);
            return (
                <div
                    className="w-8 h-8 rounded-full border border-border/60 flex-shrink-0 bg-muted"
                    style={{
                        backgroundImage: `url(${src})`,
                        backgroundSize: 'auto 230%',
                        backgroundPosition: '50% 5%',
                        backgroundRepeat: 'no-repeat',
                    }}
                    role="img"
                    aria-label={playerName}
                >
                    <img src={src} alt="" className="sr-only" onError={() => setErr(true)} />
                </div>
            );
        }
        if (!err) {
            return (
                <img
                    src={playerUrl(playerId)}
                    alt={playerName}
                    className="w-8 h-8 rounded-full object-cover object-top border border-border/60 flex-shrink-0 bg-muted"
                    onError={() => setErr(true)}
                />
            );
        }
    }
    return (
        <div className="w-8 h-8 rounded-full bg-muted border border-border/60 flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-muted-foreground leading-none">
                {(playerName || '?')[0].toUpperCase()}
            </span>
        </div>
    );
}

function EventBubble({ event, homeTeamId }) {
    const isHome = event.team_id === homeTeamId;
    const isSub  = event.type === 'substitution';
    const isOG   = event.type === 'own goal';
    const isPen  = event.type === 'penalty goal';
    /*
     * A deleted goal means one of two different things and they must not read the
     * same. VAR ruled it out and the score went down; or the goal was re-credited
     * to another scorer and the score stands. Kakita's 6th-minute goal against
     * V-Varen Nagasaki became Ominami's own goal — VAR never looked at it, and
     * labelling it "Goal Disallowed" said something untrue about the referee.
     *
     * Both keep the struck-through original, because seeing what it was changed
     * FROM is the point. Only the badge differs, and amber rather than red: a
     * correction is not the same news as a goal being taken away.
     *
     * status="deleted" isn't goal-specific — PA uses the same flag for a
     * rescinded card, a VAR-overturned penalty award, etc. "Goal Disallowed"
     * on a cancelled booking would describe a goal that was never there
     * (caught live: a deleted yellow card for Ramsey rendered as a disallowed
     * goal). Only goal-type events get the VAR/reassigned treatment; anything
     * else deleted just shows struck through with a plain "Cancelled" label.
     */
    const isGoalType   = event.type === 'goal' || event.type === 'penalty goal' || event.type === 'own goal';
    const isDisallowed = !!event.deleted && isGoalType && !event.reassigned;
    const isReassigned = !!event.deleted && isGoalType && !!event.reassigned;
    const isCancelled  = !!event.deleted && !isGoalType;
    const minStr = event.minute_extra > 0
        ? `${event.minute}+${event.minute_extra}'`
        : `${event.minute}'`;
    const { bg, border, icon, label } = isDisallowed
        ? {
            bg: 'bg-red-500/15',
            border: 'ring-red-500/60',
            icon: null,
            label: `Goal Disallowed${event.var_reason ? ' · ' + event.var_reason : ''}`,
        }
        : isReassigned
        ? {
            bg: 'bg-amber-500/15',
            border: 'ring-amber-500/60',
            // Not the VAR mark, which is gated on isDisallowed above and would
            // otherwise leave this pin empty.
            icon: '⇄',
            label: 'Scorer changed',
        }
        : isCancelled
        ? { ...eventStyle(event.type), label: 'Cancelled' }
        : eventStyle(event.type);

    // Build the detail block — shown on home or away side
    const detail = isSub ? (
        <div className={`flex items-center gap-2 ${isHome ? 'flex-row-reverse' : ''}`}>
            <EventPlayerPhoto playerId={event.player2_id} playerName={event.player2} teamId={event.team_id} />
            <div className={`leading-snug ${isHome ? 'text-right' : ''}`}>
                <span className="text-sm font-semibold text-foreground block leading-tight">
                    {event.player2 || '—'}
                </span>
                {event.player && (
                    <span className="text-xs text-muted-foreground block">
                        ↓ {event.player}
                    </span>
                )}
            </div>
        </div>
    ) : (
        <div className={`flex items-center gap-2 ${isHome ? 'flex-row-reverse' : ''}`}>
            <EventPlayerPhoto playerId={event.player_id} playerName={event.player} teamId={event.team_id} />
            <div className={`leading-snug ${isHome ? 'text-right' : ''}`}>
                <span className={`text-sm font-semibold block leading-tight ${(isDisallowed || isReassigned || isCancelled) ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {event.player || '—'}
                    {isOG && <span className="text-xs text-orange-400 ml-1">(OG)</span>}
                    {isPen && <span className="text-xs text-green-400 ml-1">(P)</span>}
                </span>
                <span className={`text-[11px] font-semibold ${isDisallowed ? 'text-red-500' : isReassigned ? 'text-amber-500' : 'text-muted-foreground font-normal'}`}>
                    {isDisallowed && <span className="mr-1">VAR:</span>}
                    {label}
                </span>
                {event.assist_player && (
                    <span className="text-[11px] text-muted-foreground/70 block leading-tight">
                        🅰 {event.assist_player}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="relative flex items-center gap-0 py-2">
            {/* Home side */}
            <div className={`flex-1 flex ${isHome ? 'justify-end' : ''} items-center pr-12`}>
                {isHome && detail}
            </div>

            {/* Centre pin */}
            <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10">
                <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center text-sm shadow-lg ring-2 ring-background ${border} overflow-hidden ${isCancelled ? 'opacity-50' : ''}`}>
                    {isDisallowed ? (
                        <img
                            src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/VAR_System_Logo.svg/960px-VAR_System_Logo.svg.png"
                            alt="VAR"
                            className="w-5 h-5 object-contain"
                        />
                    ) : (
                        <span className="leading-none">{icon}</span>
                    )}
                </div>
                <span className="text-[10px] text-muted-foreground mt-0.5 tabular-nums font-semibold">{minStr}</span>
            </div>

            {/* Away side */}
            <div className="flex-1 flex items-center pl-12">
                {!isHome && detail}
            </div>
        </div>
    );
}

// ── Betting Odds ──────────────────────────────────────────────────────────────

function decimalToFractional(dec) {
    if (!dec) return '–';
    const frac = dec - 1;
    // Common fractions lookup
    const common = { 0.5:'1/2', 0.333:'1/3', 0.25:'1/4', 0.2:'1/5', 1:'EVS',
        1.5:'6/4', 2:'2/1', 3:'3/1', 4:'4/1', 5:'5/1', 6:'6/1', 7:'7/1',
        8:'8/1', 9:'9/1', 10:'10/1', 0.667:'2/3', 0.75:'3/4', 1.25:'5/4',
        1.75:'7/4', 0.4:'2/5', 0.6:'3/5', 2.5:'5/2', 3.5:'7/2', 4.5:'9/2' };
    const rounded = Math.round(frac * 1000) / 1000;
    if (common[rounded]) return common[rounded];
    // Simple approximation
    const num = Math.round(frac * 10);
    if (num % 10 === 0) return `${num / 10}/1`;
    return `${num}/10`;
}

function BookmakerLogo({ logo, name }) {
    const [err, setErr] = useState(false);
    if (!err && logo) {
        return (
            <img src={logo} alt={name} className="h-5 w-auto max-w-[80px] object-contain"
                onError={() => setErr(true)} />
        );
    }
    return <span className="text-sm font-semibold">{name}</span>;
}

// ── Article body rendering (same pipeline as FootballHubPage) ─────────────────

function decodeEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
}

function parseTweet(embedHtml) {
    const bqM = embedHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
    if (!bqM) return { text: '', url: '' };
    const inner = bqM[1];
    const paras = [];
    const pRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let m;
    while ((m = pRe.exec(inner)) !== null) paras.push(m[1]);
    const lastPara = paras[paras.length - 1] || '';
    const tweetText = decodeEntities(paras.slice(0, -1).map(p => p.replace(/<[^>]+>/g, '')).join(' ').trim());
    const urlM = lastPara.match(/href="(https?:\/\/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)[^"]*)"/i);
    const url = urlM?.[1] || '';
    return { text: tweetText, url };
}

function processArticleBody(html) {
    if (!html) return [];
    let h = html
        .replace(/\\u003C/g, '<').replace(/\\u003E/g, '>')
        .replace(/\\u0026/g, '&').replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"');

    // Strip Kaltura video embeds — PA's player SDK isn't available to us,
    // so remove the player div and its aspect-ratio wrapper to avoid blank boxes.
    h = h.replace(/<div[^>]*\bid="kaltura_player_[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
    h = h.replace(/<div[^>]*style="[^"]*padding-bottom\s*:\s*\d+(?:\.\d+)?%[^"]*"[^>]*>\s*<\/div>/gi, '');

    // Strip StreamAMG video embeds — PA's streaming player, renders as blank space on our domain.
    h = h.replace(/<div[^>]*style="[^"]*display\s*:\s*inline-block[^"]*"[^>]*>[\s\S]*?<\/div>\s*<script[^>]*streamamg[^>]*><\/script>/gi, '');
    h = h.replace(/<script[^>]*streamamg[^>]*>[\s\S]*?<\/script>/gi, '');

    // Strip PA's injected Google Analytics tracking blocks — they fire on our domain
    // and leak PA internal dimension data; silently remove them.
    h = h.replace(/<!--\s*TRACKING START GA\s*-->[\s\S]*?<!--\s*TRACKING END GA\s*-->/gi, '');

    const segments = [];
    // PA uses both "Twitter" (legacy) and "X" (post-rebrand) as the embed type label.
    const embedRe = /<!--\s*EMBED START (Twitter|X|Instagram)\s*-->([\s\S]*?)<!--\s*EMBED END \1\s*-->/gi;
    let lastIndex = 0, match;
    while ((match = embedRe.exec(h)) !== null) {
        if (match.index > lastIndex) segments.push({ type: 'html', content: h.slice(lastIndex, match.index) });
        const platform = match[1].toLowerCase();
        if (platform === 'twitter' || platform === 'x') {
            segments.push({ type: 'tweet', ...parseTweet(match[2]) });
        } else {
            const urlM = match[2].match(/href="([^"]*instagram\.com[^"]*)"/i);
            segments.push({ type: 'instagram', url: urlM?.[1] || '' });
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < h.length) segments.push({ type: 'html', content: h.slice(lastIndex) });
    return segments.length ? segments : [{ type: 'html', content: h }];
}

function TweetEmbed({ url, text }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!url || !ref.current) return;
        const load = () => window.twttr?.widgets?.load(ref.current);
        if (window.twttr?.widgets) { load(); }
        else if (!document.getElementById('twitter-widgets-js')) {
            const s = document.createElement('script');
            s.id = 'twitter-widgets-js'; s.src = 'https://platform.twitter.com/widgets.js';
            s.async = true; s.charset = 'utf-8'; s.onload = load;
            document.head.appendChild(s);
        } else {
            const t = setInterval(() => { if (window.twttr?.widgets) { clearInterval(t); load(); } }, 100);
            setTimeout(() => clearInterval(t), 5000);
        }
    }, [url]);
    if (!url) return null;
    return (
        <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center">
            <blockquote className="twitter-tweet" data-theme="dark" data-dnt="true">
                <a href={url}>{text || 'View tweet'}</a>
            </blockquote>
        </div>
    );
}

function ArticleBody({ html }) {
    const segments = useMemo(() => processArticleBody(html), [html]);
    return (
        <div>
            {segments.map((seg, i) => {
                if (seg.type === 'tweet') return <TweetEmbed key={i} {...seg} />;
                if (seg.type === 'instagram') return (
                    <div key={i} className="border rounded-xl p-4 my-3 max-w-lg mx-auto flex items-center gap-3 bg-card">
                        <p className="text-sm text-muted-foreground">
                            Instagram post — <a href={seg.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">View on Instagram →</a>
                        </p>
                    </div>
                );
                return (
                    <div key={i}
                        className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground leading-relaxed article-body"
                        dangerouslySetInnerHTML={{ __html: seg.content }}
                    />
                );
            })}
        </div>
    );
}

// Load Twitter widgets.js and scan a container for blockquotes to render
function loadTwitterWidgets(container) {
    const load = () => window.twttr?.widgets?.load(container || undefined);
    if (window.twttr?.widgets) {
        load();
    } else if (!document.getElementById('twitter-widgets-js')) {
        const s = document.createElement('script');
        s.id = 'twitter-widgets-js';
        s.src = 'https://platform.twitter.com/widgets.js';
        s.async = true;
        s.charset = 'utf-8';
        s.onload = load;
        document.head.appendChild(s);
    } else {
        const t = setInterval(() => {
            if (window.twttr?.widgets) { clearInterval(t); load(); }
        }, 100);
        setTimeout(() => clearInterval(t), 5000);
    }
}

function ArticleModal({ articleId, onClose }) {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!articleId) return;
        setLoading(true);
        setArticle(null);
        axios.get(`${API_URL}/api/football/hub/article`, {
            params: { item_id: articleId },
            headers: authHeaders(),
        }).then(r => setArticle(r.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
    }, [articleId]);

    const imageUrl = article?.associations?.featureimage?.renditions?.['16x9']?.href
        || article?.associations?.featureimage?.renditions?.['4x3']?.href || null;

    const tags = (article?.subject || [])
        .filter(s => ['sport', 'tag', 'competition'].includes(s.profile))
        .slice(0, 5);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
                    <DialogTitle className="pr-8 text-base font-bold leading-snug uppercase">
                        {loading ? <Skeleton className="h-5 w-3/4" /> : article?.headline}
                    </DialogTitle>
                </DialogHeader>
                {loading ? (
                    <div className="p-6 space-y-3">
                        <Skeleton className="h-48 w-full" />
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                ) : !article || article.error ? (
                    <div className="p-6 text-center text-muted-foreground">Could not load article.</div>
                ) : (
                    <div className="p-6 space-y-4">
                        {imageUrl && <img src={imageUrl} alt={article.headline} className="w-full rounded-lg object-cover max-h-72" />}
                        <div className="border-b pb-3">
                            {article.byline && <p className="text-sm italic text-muted-foreground">{article.byline}</p>}
                            {article.issued && <p className="text-xs text-muted-foreground mt-0.5">{format(parseISO(article.issued), 'EEE d MMM yyyy, HH:mm')}</p>}
                        </div>
                        {article.description_text && <p className="font-semibold text-foreground leading-snug">{article.description_text}</p>}
                        {(article.body_html || article.body_text) && (
                            <ArticleBody html={article.body_html || `<p>${article.body_text}</p>`} />
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                                {tags.map(t => <Badge key={t.qcode || t.name} variant="secondary" className="text-xs">{t.name}</Badge>)}
                            </div>
                        )}
                        <div className="pt-2 border-t">
                            <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function MatchStories({ m }) {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openArticleId, setOpenArticleId] = useState(null);

    useEffect(() => {
        if (!m?.home_team_name || !m?.away_team_name) { setLoading(false); return; }
        const params = new URLSearchParams({
            home_team: m.home_team_name,
            away_team: m.away_team_name,
            limit: 3,
        });
        axios.get(`${API_URL}/api/football/hub/match-stories?${params}`, { headers: authHeaders() })
            .then(r => setStories(r.data?.items || []))
            .catch(() => setStories([]))
            .finally(() => setLoading(false));
    }, [m?.home_team_name, m?.away_team_name]);

    if (loading) {
        return (
            <div className="rounded-2xl border bg-card overflow-hidden">
                <div className="px-4 py-3 border-b">
                    <div className="h-3 bg-muted rounded w-24 animate-pulse" />
                </div>
                <div className="grid grid-cols-3 gap-0 divide-x divide-border/50">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex flex-col animate-pulse">
                            <div className="aspect-[4/3] bg-muted" />
                            <div className="p-3 space-y-1.5">
                                <div className="h-3 bg-muted rounded w-full" />
                                <div className="h-3 bg-muted rounded w-2/3" />
                                <div className="h-2.5 bg-muted rounded w-1/3 mt-1" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!stories.length) return null;

    function extractId(item) {
        const href = item?.links?.[0]?.href || '';
        const segs = href.split('/');
        return segs[segs.length - 1] || item?.uri || null;
    }
    function storyImage(item) {
        return item?.associations?.featureimage?.renditions?.['4x3']?.href
            || item?.associations?.featureimage?.renditions?.['16x9']?.href
            || null;
    }
    function fmtDate(iso) {
        if (!iso) return '';
        try { return format(parseISO(iso), 'd MMM'); } catch { return ''; }
    }

    return (
        <>
        {openArticleId && <ArticleModal articleId={openArticleId} onClose={() => setOpenArticleId(null)} />}
        <div className="rounded-2xl border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Related Stories</p>
            </div>
            <div className="grid grid-cols-3 divide-x divide-border/50">
                {stories.map((item, i) => {
                    const img = storyImage(item);
                    const id = extractId(item);
                    return (
                        <button
                            key={id || i}
                            onClick={() => id && setOpenArticleId(id)}
                            className="flex flex-col group hover:bg-muted/30 transition-colors text-left"
                        >
                            {/* Image */}
                            <div className="aspect-[4/3] overflow-hidden bg-muted w-full">
                                {img ? (
                                    <img src={img} alt={item.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    </div>
                                )}
                            </div>
                            {/* Headline + date */}
                            <div className="p-3 flex-1 flex flex-col">
                                <p className="text-xs font-semibold leading-snug line-clamp-3 group-hover:text-primary transition-colors flex-1">{item.headline}</p>
                                <p className="text-[11px] text-muted-foreground mt-2">{fmtDate(item.issued || item.firstcreated)}</p>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
        </>
    );
}

function OddsSection({ matchId, m, compId }) {
    const [odds, setOdds] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!matchId || !m?.home_team_name || !m?.away_team_name) { setLoading(false); return; }
        const params = new URLSearchParams({
            home_team:    m.home_team_name,
            away_team:    m.away_team_name,
            comp_id:      compId || '100',
            kickoff_time: m.kickoff_time || '',
        });
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/odds?${params}`, { headers: authHeaders() })
            .then(r => setOdds(r.data))
            .catch(() => setOdds(null))
            .finally(() => setLoading(false));
    }, [matchId, m?.home_team_name, m?.away_team_name, m?.kickoff_time, compId]);

    if (loading) return (
        <div className="rounded-2xl border bg-card p-3 space-y-2 animate-pulse h-full">
            <div className="h-3 bg-muted rounded w-20 mb-2" />
            {[1,2,3].map(i => <div key={i} className="h-8 bg-muted rounded-lg" />)}
        </div>
    );

    if (!odds?.bookmakers?.length) return null;

    const homeShort = m.home_team_name?.split(' ')[0];
    const awayShort = m.away_team_name?.split(' ')[0];

    return (
        <div className="rounded-2xl border bg-card overflow-hidden flex flex-col h-full">
            {/* Header */}
            <div className="px-3 py-2.5 border-b">
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Match Odds</p>
                {/* Column label row */}
                <div className="grid grid-cols-[1fr_repeat(3,_auto)] gap-x-1.5 mt-1.5 text-[10px] font-semibold text-muted-foreground/70">
                    <span></span>
                    <span className="w-10 text-center truncate">{homeShort}</span>
                    <span className="w-7 text-center">D</span>
                    <span className="w-10 text-center truncate">{awayShort}</span>
                </div>
            </div>

            {/* Bookmaker rows — compact */}
            <div className="divide-y divide-border/50 flex-1">
                {odds.bookmakers.map(bm => (
                    <div key={bm.key} className="grid grid-cols-[1fr_repeat(3,_auto)] gap-x-1.5 px-3 py-2 items-center">
                        <span className="text-[11px] font-medium truncate text-foreground/80">{bm.name}</span>
                        <span className="w-10 text-center text-[12px] font-bold text-primary tabular-nums">
                            {decimalToFractional(bm.home_odds)}
                        </span>
                        <span className="w-7 text-center text-[11px] text-muted-foreground tabular-nums">
                            {bm.draw_odds ? decimalToFractional(bm.draw_odds) : '–'}
                        </span>
                        <span className="w-10 text-center text-[12px] font-bold text-primary tabular-nums">
                            {decimalToFractional(bm.away_odds)}
                        </span>
                    </div>
                ))}
            </div>

            {/* Compact responsible gambling footer */}
            <div className="px-3 py-2 border-t bg-muted/20 flex items-center gap-2">
                <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center bg-[#004f9e] text-white text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0">
                    BeGambleAware
                </a>
                <span className="text-[10px] text-muted-foreground leading-tight">18+ | Play responsibly</span>
            </div>
        </div>
    );
}

// Standalone form card — fetches H2H data independently (cached on backend)
function H2HFormCard({ matchId, m }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!matchId) return;
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/h2h`, { headers: authHeaders() })
            .then(r => setData(r.data))
            .catch(() => {});
    }, [matchId]);

    const nameA = m?.home_team_name || data?.team_a?.name || '';
    const nameB = m?.away_team_name || data?.team_b?.name || '';

    const meetings = data?.meetings || [];

    function derivedForm(teamName) {
        if (!teamName || !meetings.length) return [];
        return meetings.slice(0, 6).map(mtg => {
            if (mtg.home === teamName) return mtg.home_score > mtg.away_score ? 'W' : mtg.home_score === mtg.away_score ? 'D' : 'L';
            if (mtg.away === teamName) return mtg.away_score > mtg.home_score ? 'W' : mtg.away_score === mtg.home_score ? 'D' : 'L';
            return null;
        }).filter(Boolean);
    }

    const formA = derivedForm(nameA);
    const formB = derivedForm(nameB);

    const dot = (r, i) => {
        const c = { W: 'bg-emerald-500', D: 'bg-amber-400', L: 'bg-rose-500' };
        return <span key={i} className={`inline-block w-3 h-3 rounded-full ${c[r] || 'bg-muted'}`} />;
    };

    if (!meetings.length) return null;

    return (
        <div className="rounded-2xl border bg-card overflow-hidden flex flex-col h-full">
            <div className="px-3 py-2.5 border-b">
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">H2H Form</p>
            </div>
            <div className="p-3 space-y-3 flex-1">
                {[{ name: nameA, form: formA }, { name: nameB, form: formB }].map(({ name, form }) => (
                    <div key={name}>
                        <p className="text-[11px] font-semibold truncate mb-1.5">{name}</p>
                        <div className="flex gap-1.5 flex-wrap">
                            {form.length > 0
                                ? form.map((r, i) => dot(r, i))
                                : <span className="text-[10px] text-muted-foreground">No data</span>
                            }
                        </div>
                        {form.length > 0 && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                                {form.filter(r => r === 'W').length}W &nbsp;
                                {form.filter(r => r === 'D').length}D &nbsp;
                                {form.filter(r => r === 'L').length}L
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function MatchInsightsCard({ matchId, m, compId, showOdds }) {
    const [h2h, setH2h]   = useState(null);
    const [odds, setOdds] = useState(null);
    const [h2hLoading, setH2hLoading]   = useState(true);
    const [oddsLoading, setOddsLoading] = useState(!!showOdds);

    useEffect(() => {
        if (!matchId) return;
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/h2h`, { headers: authHeaders() })
            .then(r => setH2h(r.data))
            .catch(() => setH2h(null))
            .finally(() => setH2hLoading(false));
    }, [matchId]);

    useEffect(() => {
        if (!showOdds || !matchId || !m?.home_team_name || !m?.away_team_name) {
            setOddsLoading(false);
            return;
        }
        const params = new URLSearchParams({
            home_team: m.home_team_name, away_team: m.away_team_name,
            comp_id: compId || '100', kickoff_time: m.kickoff_time || '',
        });
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/odds?${params}`, { headers: authHeaders() })
            .then(r => setOdds(r.data))
            .catch(() => setOdds(null))
            .finally(() => setOddsLoading(false));
    }, [matchId, showOdds, m?.home_team_name, m?.away_team_name, m?.kickoff_time, compId]);

    const loading = h2hLoading || oddsLoading;
    const hasOdds = odds?.bookmakers?.length > 0;
    const hasH2h  = h2h && !h2h.error && h2h.meetings?.length > 0;

    if (loading) return (
        <div className="rounded-2xl border bg-card p-4 space-y-3 animate-pulse">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
        </div>
    );

    if (!hasOdds && !hasH2h) return null;

    const { team_a, team_b, record, stats, meetings } = h2h || {};
    const nameA = m?.home_team_name || team_a?.name || '';
    const nameB = m?.away_team_name || team_b?.name || '';

    const homeShort = nameA.split(' ')[0];
    const awayShort = nameB.split(' ')[0];

    function fmtDate(raw) {
        try {
            const norm = raw?.length === 8
                ? `${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)}`
                : raw;
            return format(parseISO(norm), 'd MMM yyyy');
        } catch { return raw || ''; }
    }

    return (
        <div className="rounded-2xl border bg-card overflow-hidden">

            {/* ── Win Probability (above odds table) ── */}
            {hasOdds && (() => {
                const bms = odds.bookmakers.filter(b => b.home_odds && b.away_odds);
                if (!bms.length) return null;
                const avgH = bms.reduce((s, b) => s + 1/b.home_odds, 0) / bms.length;
                const avgD = bms.reduce((s, b) => s + (b.draw_odds ? 1/b.draw_odds : 0), 0) / bms.length;
                const avgA = bms.reduce((s, b) => s + 1/b.away_odds, 0) / bms.length;
                const tot = avgH + avgD + avgA;
                const pH = Math.round(avgH / tot * 100);
                const pD = Math.round(avgD / tot * 100);
                const pA = 100 - pH - pD;
                return (
                    <div className="px-4 py-3 border-b bg-muted/10">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Win Probability (consensus)</p>
                        <div className="flex items-center justify-between text-xs font-bold mb-1.5">
                            <span className="text-emerald-500">{nameA} {pH}%</span>
                            <span className="text-muted-foreground">Draw {pD}%</span>
                            <span className="text-rose-500">{pA}% {nameB}</span>
                        </div>
                        <div className="flex h-2.5 rounded-full overflow-hidden gap-px">
                            <div className="bg-emerald-500 transition-all" style={{ width: `${pH}%` }} />
                            <div className="bg-amber-400 transition-all"   style={{ width: `${pD}%` }} />
                            <div className="bg-rose-500 transition-all"    style={{ width: `${pA}%` }} />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1 text-center">Implied from bookmaker odds · margin removed</p>
                    </div>
                );
            })()}

            {/* ── Match Odds ── */}
            {hasOdds && (
                <>
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Match Odds</span>
                        <span className="text-[10px] text-muted-foreground/60">18+ | Odds subject to change</span>
                    </div>
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-2 bg-muted/30 text-[11px] font-semibold text-muted-foreground">
                        <span>Bookmaker</span>
                        <span className="w-14 text-center">{homeShort}</span>
                        <span className="w-10 text-center">Draw</span>
                        <span className="w-14 text-center">{awayShort}</span>
                    </div>
                    <div className="divide-y divide-border/50">
                        {odds.bookmakers.map(bm => (
                            <div key={bm.key} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-2.5 items-center hover:bg-muted/20 transition-colors">
                                <BookmakerLogo logo={bm.logo} name={bm.name} />
                                <span className="w-14 text-center text-sm font-bold text-primary tabular-nums">{decimalToFractional(bm.home_odds)}</span>
                                <span className="w-10 text-center text-sm text-muted-foreground tabular-nums">{bm.draw_odds ? decimalToFractional(bm.draw_odds) : '–'}</span>
                                <span className="w-14 text-center text-sm font-bold text-primary tabular-nums">{decimalToFractional(bm.away_odds)}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* ── Head to Head ── */}
            {hasH2h && (
                <>
                    <div className={`px-4 py-3 flex items-center justify-between ${hasOdds ? 'border-t border-b' : 'border-b'}`}>
                        <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Head to Head</span>
                        <span className="text-xs text-muted-foreground">{record.total} meetings</span>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* Win likelihood bar — hidden when bookmaker win probability is shown */}
                        {!hasOdds && stats.win_pct_a !== null && (
                            <div>
                                <div className="flex justify-between text-xs font-semibold mb-1.5">
                                    <span className="text-emerald-500">{nameA} {stats.win_pct_a}%</span>
                                    <span className="text-muted-foreground">Draw {stats.draw_pct}%</span>
                                    <span className="text-rose-500">{stats.win_pct_b}% {nameB}</span>
                                </div>
                                <div className="flex h-2.5 rounded-full overflow-hidden gap-px">
                                    <div className="bg-emerald-500" style={{ width: `${stats.win_pct_a}%` }} />
                                    <div className="bg-amber-400"   style={{ width: `${stats.draw_pct}%` }} />
                                    <div className="bg-rose-500"    style={{ width: `${stats.win_pct_b}%` }} />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1 text-center">Based on last {record.total} meetings</p>
                            </div>
                        )}

                        {/* Key stats grid */}
                        <div className="grid grid-cols-3 gap-2 text-center">
                            {[
                                { val: stats.avg_goals_per_match ?? '—',    label: 'Avg goals\nper match' },
                                { val: stats.over_2_5 != null ? `${Math.round(stats.over_2_5 / record.total * 100)}%` : '—', label: 'Over 2.5\ngoals' },
                                { val: stats.both_teams_scored != null ? `${Math.round(stats.both_teams_scored / record.total * 100)}%` : '—', label: 'Both teams\nscored' },
                                { val: stats.avg_bookings_per_match ?? '—', label: 'Avg bookings\nper match' },
                                { val: stats.avg_reds_per_match ?? '—',     label: 'Avg red cards\nper match' },
                                { val: record.total > 0 ? `${Math.round((record.team_a_wins + record.team_b_wins) / record.total * 100)}%` : '—', label: 'Decisive\nresults' },
                            ].map(({ val, label }) => (
                                <div key={label} className="bg-muted/40 rounded-lg p-2.5">
                                    <p className="text-lg font-bold">{val}</p>
                                    <p className="text-[10px] text-muted-foreground leading-tight">{label.split('\n').map((l, i) => <span key={i}>{l}{i === 0 && <br/>}</span>)}</p>
                                </div>
                            ))}
                        </div>

                        {/* Recent meetings */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-2">Recent Meetings</p>
                            <div className="space-y-1.5">
                                {meetings.map((mtg, i) => {
                                    const isHomeWin = mtg.home_score > mtg.away_score;
                                    const isDraw    = mtg.home_score === mtg.away_score;
                                    return (
                                        <div key={i} className="flex items-center gap-2 text-xs">
                                            <span className="text-muted-foreground w-[88px] shrink-0 tabular-nums text-[11px]">{fmtDate(mtg.date)}</span>
                                            <span className="flex-1 text-right truncate">{mtg.home}</span>
                                            <span className={`font-bold tabular-nums px-1.5 py-0.5 rounded text-[11px] ${
                                                isDraw    ? 'bg-amber-500/20 text-amber-500' :
                                                isHomeWin ? 'bg-emerald-500/20 text-emerald-500' : 'bg-rose-500/20 text-rose-500'
                                            }`}>{mtg.home_score}–{mtg.away_score}</span>
                                            <span className="flex-1 truncate">{mtg.away}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* BeGambleAware footer */}
            <div className="px-4 py-3 bg-muted/20 border-t flex flex-wrap items-center gap-2">
                <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 bg-[#004f9e] text-white text-[11px] font-bold px-2.5 py-1 rounded-md hover:opacity-90 transition-opacity flex-shrink-0">
                    BeGambleAware.org
                </a>
                <span className="text-[11px] text-muted-foreground">18+ only. Gambling can be addictive — please play responsibly. Call <strong>0808 8020 133</strong> for free support.</span>
            </div>
        </div>
    );
}

function TeamPreviewCard({ matchId, m }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!matchId) return;
        axios.get(`${API_URL}/api/football/match/${matchId}/team-preview`, { headers: authHeaders() })
            .then(r => setData(r.data))
            .catch(() => {});
    }, [matchId]);

    if (!data) return null;
    const { home, away } = data;
    if (!home?.form?.length && !away?.form?.length) return null;

    const dot = (r, i) => {
        const colors = { W: 'bg-emerald-500', D: 'bg-amber-400', L: 'bg-rose-500' };
        return (
            <span key={i} title={r}
                className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-black text-white ${colors[r] || 'bg-muted'}`}>
                {r}
            </span>
        );
    };

    const formRow = (team) => {
        if (!team?.form?.length) return null;
        const recent = [...team.form].reverse(); // most recent last → show left=oldest right=newest
        return (
            <div>
                <div className="flex items-center gap-1.5 mb-1">
                    <TeamBadgeLight teamId={team.team_id} teamName={team.team_name} />
                    <span className="text-xs font-semibold truncate">{team.team_name}</span>
                    <span className="text-[10px] text-muted-foreground ml-1">Last 5</span>
                </div>
                <div className="flex items-center gap-1.5 mb-1.5">
                    {recent.map((r, i) => dot(r.result, i))}
                </div>
                {recent.length > 0 && (
                    <div className="space-y-0.5">
                        {recent.map((r, i) => {
                            const isHome = r.is_home;
                            const won = r.result === 'W';
                            const lost = r.result === 'L';
                            return (
                                <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                    <span className={`w-5 text-center text-[9px] font-bold rounded px-0.5 ${won ? 'bg-emerald-500/20 text-emerald-500' : lost ? 'bg-rose-500/20 text-rose-500' : 'bg-amber-400/20 text-amber-500'}`}>{r.result}</span>
                                    <span className="text-muted-foreground/60 text-[9px] flex-shrink-0">{isHome ? 'H' : 'A'}</span>
                                    <span className="truncate flex-1">{r.opponent}</span>
                                    <span className="font-mono flex-shrink-0">{r.home_score}–{r.away_score}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // Season stats comparison
    const hasStats = (home.stats && Object.keys(home.stats).some(k => home.stats[k] != null)) ||
                     (away.stats && Object.keys(away.stats).some(k => away.stats[k] != null));

    const statRow = (label, hVal, aVal, higherIsBetter = true) => {
        if (hVal == null && aVal == null) return null;
        const hN = parseFloat(hVal) || 0;
        const aN = parseFloat(aVal) || 0;
        const hBetter = higherIsBetter ? hN >= aN : hN <= aN;
        const aBetter = higherIsBetter ? aN >= hN : aN <= hN;
        return (
            <div key={label} className="flex items-center gap-2 text-xs">
                <span className={`font-bold w-10 text-right tabular-nums ${hBetter ? 'text-foreground' : 'text-muted-foreground'}`}>{hVal ?? '–'}</span>
                <span className="flex-1 text-center text-[10px] text-muted-foreground">{label}</span>
                <span className={`font-bold w-10 text-left tabular-nums ${aBetter ? 'text-foreground' : 'text-muted-foreground'}`}>{aVal ?? '–'}</span>
            </div>
        );
    };

    return (
        <div className="rounded-2xl border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Team Form</p>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
                {formRow(home)}
                {formRow(away)}
            </div>
            {hasStats && (
                <>
                    <div className="px-4 pb-1 pt-1 border-t">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] text-muted-foreground font-semibold truncate">{home.team_name}</span>
                            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">Season</span>
                            <span className="text-[10px] text-muted-foreground font-semibold truncate text-right">{away.team_name}</span>
                        </div>
                        <div className="space-y-1.5 pb-3">
                            {[
                                statRow('Played',      home.stats?.played,         away.stats?.played,         false),
                                statRow('Wins',        home.stats?.wins,            away.stats?.wins),
                                statRow('Goals For',   home.stats?.goals_for,       away.stats?.goals_for),
                                statRow('Goals Ag.',   home.stats?.goals_against,   away.stats?.goals_against,  false),
                                statRow('xG For',      home.stats?.xg_for != null ? Number(home.stats.xg_for).toFixed(1) : null,
                                                       away.stats?.xg_for != null ? Number(away.stats.xg_for).toFixed(1) : null),
                                statRow('Position',    home.stats?.position,        away.stats?.position,       false),
                            ].filter(Boolean)}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ── Match Preview Card (scorelines, goalscorers, BTTS, xG) ───────────────────

function MatchPreviewCard({ matchId, m, compId }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!matchId || !m?.home_team_name) { setLoading(false); return; }
        const params = new URLSearchParams({
            home_team:    m.home_team_name,
            away_team:    m.away_team_name,
            comp_id:      compId || '100',
            kickoff_time: m.kickoff_time || '',
        });
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/preview?${params}`, { headers: authHeaders() })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [matchId, m?.home_team_name, m?.away_team_name, m?.kickoff_time, compId]);

    const hasScorers = data?.goalscorers?.length > 0;
    const hasBtts    = data?.btts_yes_prob != null;
    const hasXg      = data?.xg_home != null || data?.xg_away != null;

    if (loading) return (
        <div className="rounded-2xl border bg-card p-4 space-y-3 animate-pulse">
            <div className="h-3 bg-muted rounded w-28" />
            <div className="grid grid-cols-2 gap-3">
                {[1,2,3,4].map(i => <div key={i} className="h-10 bg-muted rounded-xl" />)}
            </div>
        </div>
    );

    if (!hasScorers && !hasBtts && !hasXg) return null;

    const homeShort = m?.home_team_name?.split(' ')[0] || '';
    const awayShort = m?.away_team_name?.split(' ')[0] || '';

    // Colour band for probability bars
    function probColour(p) {
        if (p >= 0.25) return 'bg-emerald-500';
        if (p >= 0.15) return 'bg-amber-400';
        return 'bg-rose-500/70';
    }

    return (
        <div className="rounded-2xl border bg-card overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    Match Preview
                </p>
                <span className="text-[10px] text-muted-foreground/60">
                    Implied from odds · 18+ · BeGambleAware
                </span>
            </div>

            <div className="p-4 space-y-5">

                {/* xG row */}
                {hasXg && (
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                            Expected Goals (season avg)
                        </p>
                        <div className="flex items-center gap-3">
                            <div className="flex-1 text-right">
                                <span className="text-lg font-bold tabular-nums">
                                    {data.xg_home ?? '–'}
                                </span>
                                <p className="text-[10px] text-muted-foreground truncate">{homeShort}</p>
                            </div>
                            <span className="text-[10px] font-semibold text-muted-foreground">xG</span>
                            <div className="flex-1 text-left">
                                <span className="text-lg font-bold tabular-nums">
                                    {data.xg_away ?? '–'}
                                </span>
                                <p className="text-[10px] text-muted-foreground truncate">{awayShort}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top goalscorers — 2-col grid */}
                {hasScorers && (
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                            Anytime Goalscorer
                        </p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                            {data.goalscorers.slice(0, 6).map(({ name, prob }) => {
                                const parts = name.split(' ');
                                const display = parts.length > 1
                                    ? `${parts[0][0]}. ${parts.slice(1).join(' ')}`
                                    : name;
                                return (
                                    <div key={name}>
                                        <div className="flex items-center justify-between mb-0.5">
                                            <span className="text-xs font-medium truncate max-w-[90px]">{display}</span>
                                            <span className="text-[11px] text-muted-foreground tabular-nums">
                                                {Math.round(prob * 100)}%
                                            </span>
                                        </div>
                                        <div className="h-1 rounded-full bg-muted overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all ${probColour(prob)}`}
                                                style={{ width: `${Math.min(prob * 100 * 3, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* BTTS */}
                {hasBtts && (
                    <div className="rounded-xl bg-muted/40 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-base">⚽⚽</span>
                            <div>
                                <p className="text-xs font-semibold">Both Teams to Score</p>
                                <p className="text-[10px] text-muted-foreground">Anytime</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-lg font-bold tabular-nums text-primary">
                                {Math.round(data.btts_yes_prob * 100)}%
                            </p>
                            <p className="text-[10px] text-muted-foreground">chance</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Penalty shootout ──────────────────────────────────────────────────────────

function PenaltyShootout({ matchId, m, isLive }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!matchId) return;
        const load = () =>
            axios.get(`${API_URL}/api/football/hub/match/${matchId}/shootout`, { headers: authHeaders() })
                .then(r => { if (r.data?.available) setData(r.data); })
                .catch(() => {});
        load();
        // Keep polling while live so new penalties appear
        if (!isLive) return;
        const iv = setInterval(load, 30000);
        return () => clearInterval(iv);
    }, [matchId, isLive]);

    if (!data?.available) return null;

    const { home, away } = data;
    const homePens = home?.penalties || [];
    const awayPens = away?.penalties || [];
    const rows = Math.max(homePens.length, awayPens.length);

    const outcomeIcon = (outcome) => {
        if (outcome === 'scored') return <span className="text-green-500 text-base leading-none">⚽</span>;
        if (outcome === 'missed') return <span className="text-red-500 font-black text-sm leading-none">✕</span>;
        if (outcome === 'saved')  return <span className="text-amber-400 font-black text-sm leading-none">●</span>;
        return null;
    };

    const outcomeLabel = (outcome) => {
        if (outcome === 'scored') return 'Scored';
        if (outcome === 'missed') return 'Missed';
        if (outcome === 'saved')  return 'Saved';
        return '';
    };

    return (
        <div className="rounded-2xl border bg-card overflow-hidden">
            {/* Header */}
            <div className="flex items-center px-4 pt-3 pb-2 border-b">
                <div className="flex-1 flex items-center gap-1.5">
                    <TeamBadgeLight teamId={m?.home_team_id} teamName={m?.home_team_name} />
                    <span className="text-xs font-semibold text-muted-foreground truncate max-w-[100px]">
                        {home?.team_name || m?.home_team_name}
                    </span>
                </div>
                <span className="text-xs text-muted-foreground font-medium mx-2 shrink-0">
                    Penalty Shootout
                    {home?.penalties_scored != null && away?.penalties_scored != null && (
                        <span className="ml-2 font-bold text-foreground">
                            {home.penalties_scored} – {away.penalties_scored}
                        </span>
                    )}
                </span>
                <div className="flex-1 flex items-center justify-end gap-1.5">
                    <span className="text-xs font-semibold text-muted-foreground truncate max-w-[100px] text-right">
                        {away?.team_name || m?.away_team_name}
                    </span>
                    <TeamBadgeLight teamId={m?.away_team_id} teamName={m?.away_team_name} />
                </div>
            </div>

            {/* Penalty rows */}
            <div className="px-4 py-3 relative space-y-2">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border/50 -translate-x-1/2 pointer-events-none" />
                {Array.from({ length: rows }).map((_, i) => {
                    const hp = homePens[i];
                    const ap = awayPens[i];
                    return (
                        <div key={i} className="relative flex items-center gap-0 py-1">
                            {/* Home side */}
                            <div className="flex-1 flex justify-end items-center pr-12">
                                {hp && (
                                    <div className="flex items-center gap-2">
                                        <div className="text-right leading-snug">
                                            <span className="text-sm font-semibold text-foreground block leading-tight">
                                                {hp.name.split(' ').slice(-1)[0]}
                                            </span>
                                            <span className={`text-[10px] ${hp.outcome === 'scored' ? 'text-green-500' : hp.outcome === 'saved' ? 'text-amber-400' : 'text-red-500'}`}>
                                                {outcomeLabel(hp.outcome)}
                                            </span>
                                        </div>
                                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                            {outcomeIcon(hp.outcome)}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Centre round number (kicks are now in true chronological order) */}
                            <div className="absolute left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-muted border border-border flex items-center justify-center z-10">
                                <span className="text-[10px] font-bold text-muted-foreground">{i + 1}</span>
                            </div>
                            {/* Away side */}
                            <div className="flex-1 flex justify-start items-center pl-12">
                                {ap && (
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                            {outcomeIcon(ap.outcome)}
                                        </div>
                                        <div className="leading-snug">
                                            <span className="text-sm font-semibold text-foreground block leading-tight">
                                                {ap.name.split(' ').slice(-1)[0]}
                                            </span>
                                            <span className={`text-[10px] ${ap.outcome === 'scored' ? 'text-green-500' : ap.outcome === 'saved' ? 'text-amber-400' : 'text-red-500'}`}>
                                                {outcomeLabel(ap.outcome)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {/* Legend */}
                <div className="flex justify-center gap-4 pt-2 border-t border-border/40 mt-2">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><span className="text-green-500">⚽</span> Scored</span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><span className="text-red-500 font-black">✕</span> Missed</span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><span className="text-amber-400 font-black">●</span> Saved</span>
                </div>
            </div>
        </div>
    );
}

// ── Referee Card ──────────────────────────────────────────────────────────────
// Mapping of non-standard country codes from TheStatsAPI to flagcdn ISO codes
const STATSAPI_CC_TO_ISO = {
    EN: 'gb-eng', SC: 'gb-sct', WA: 'gb-wls', NI: 'gb-nir',
};

function getRefereeFlag(countryCode) {
    if (!countryCode) return null;
    const upper = countryCode.toUpperCase();
    const iso = STATSAPI_CC_TO_ISO[upper] || countryCode.toLowerCase();
    return `https://flagcdn.com/20x15/${iso}.png`;
}

function RefereeCard({ matchId }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!matchId) { setLoading(false); return; }
        axios.get(`${API_URL}/api/football/match/${matchId}/referee`, { headers: authHeaders() })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [matchId]);

    if (loading) return (
        <div className="rounded-2xl border bg-card p-4 animate-pulse">
            <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-32" />
            </div>
        </div>
    );

    if (!data?.available) return null;

    const ref   = data.referee || {};
    const games = ref.career?.games ?? 0;
    const flagUrl = getRefereeFlag(ref.country_code);

    const StatPill = ({ label, value, colorClass }) => (
        <div className="flex flex-col items-center gap-0.5 bg-muted/50 rounded-xl px-3 py-2 min-w-[72px]">
            <span className={`text-lg font-bold leading-none ${colorClass}`}>{value ?? '—'}</span>
            <span className="text-[10px] text-muted-foreground text-center leading-tight mt-0.5">{label}</span>
        </div>
    );

    return (
        <div className="rounded-2xl border bg-card overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Referee</span>
                <div className="flex items-center gap-2 ml-1">
                    {flagUrl && (
                        <img
                            src={flagUrl}
                            alt={ref.country || ''}
                            className="rounded-sm"
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <span className="text-sm font-semibold text-foreground">{ref.name}</span>
                    {ref.country && (
                        <span className="text-xs text-muted-foreground">({ref.country})</span>
                    )}
                </div>
            </div>

            {/* Stats row */}
            <div className="px-4 py-3">
                <p className="text-[10px] text-muted-foreground mb-2.5 uppercase tracking-wider font-medium">
                    Avg per game · {games} match{games !== 1 ? 'es' : ''} officiated
                </p>
                <div className="flex gap-2 flex-wrap">
                    <StatPill
                        label="Bookings PG"
                        value={ref.avg_bookings}
                        colorClass="text-amber-400"
                    />
                    <StatPill
                        label="YC PG"
                        value={ref.avg_yellow_cards}
                        colorClass="text-yellow-400"
                    />
                    <StatPill
                        label="RC PG"
                        value={ref.avg_red_cards}
                        colorClass="text-red-500"
                    />
                </div>
            </div>
        </div>
    );
}

function SummaryTab({ events, homeTeamId, m, isFinished, isLive, matchId, compId }) {
    if (!events?.length) {
        return (
            <div className="space-y-4">
                <RefereeCard matchId={matchId} />
                <PenaltyShootout matchId={matchId} m={m} isLive={isLive} />
                <MatchInsightsCard matchId={matchId} m={m} compId={compId} showOdds={!isFinished && !isLive} />
                {!isFinished && !isLive && <MatchPreviewCard matchId={matchId} m={m} compId={compId} />}
                {!isFinished && !isLive && <TeamPreviewCard matchId={matchId} m={m} />}
                <MatchStories m={m} />
            </div>
        );
    }

    // Split into phases — filter out standalone assist events (assister shown on goal row)
    const displayEvents = events.filter(e => e.type !== 'assist');
    const firstHalf    = displayEvents.filter(e => e.minute <= 45);
    const secondHalf   = displayEvents.filter(e => e.minute > 45 && e.minute <= 90);
    const etFirstHalf  = displayEvents.filter(e => e.minute > 90 && e.minute <= 105);
    const etSecondHalf = displayEvents.filter(e => e.minute > 105);
    const hasET        = etFirstHalf.length > 0 || etSecondHalf.length > 0;
    const hasSecond    = secondHalf.length > 0;

    const PhaseDivider = ({ label, highlight }) => (
        <div className="relative flex items-center my-3 z-10">
            <div className={`flex-1 h-px ${highlight ? 'bg-amber-500/40' : 'bg-border'}`} />
            <span className={`mx-3 text-[11px] font-semibold uppercase tracking-wider bg-card px-2 ${highlight ? 'text-amber-400' : 'text-muted-foreground'}`}>
                {label}
            </span>
            <div className={`flex-1 h-px ${highlight ? 'bg-amber-500/40' : 'bg-border'}`} />
        </div>
    );

    return (
        <div className="space-y-4">
        <div className="rounded-2xl border bg-card overflow-hidden">
            {/* Header row */}
            <div className="flex items-center px-4 pt-3 pb-2 border-b">
                <div className="flex-1 flex items-center gap-1.5">
                    <TeamBadgeLight teamId={m?.home_team_id} teamName={m?.home_team_name} />
                    <span className="text-xs font-semibold text-muted-foreground truncate max-w-[100px]">
                        {m?.home_team_name}
                    </span>
                </div>
                <span className="text-xs text-muted-foreground font-medium mx-2 shrink-0">Match Events</span>
                <div className="flex-1 flex items-center justify-end gap-1.5">
                    <span className="text-xs font-semibold text-muted-foreground truncate max-w-[100px] text-right">
                        {m?.away_team_name}
                    </span>
                    <TeamBadgeLight teamId={m?.away_team_id} teamName={m?.away_team_name} />
                </div>
            </div>

            <div className="px-4 py-2 relative">
                {/* Vertical centre line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border/50 -translate-x-1/2 pointer-events-none" />

                {/* First half */}
                {firstHalf.length > 0 && (
                    <div className="space-y-0 mb-2">
                        {firstHalf.map((ev, i) => (
                            <EventBubble key={i} event={ev} homeTeamId={homeTeamId} />
                        ))}
                    </div>
                )}

                {/* Half Time divider */}
                {(firstHalf.length > 0 || hasSecond || hasET) && (
                    <PhaseDivider label="Half Time" />
                )}

                {/* Second half */}
                {hasSecond && (
                    <div className="space-y-0 mb-2">
                        {secondHalf.map((ev, i) => (
                            <EventBubble key={`sh-${i}`} event={ev} homeTeamId={homeTeamId} />
                        ))}
                    </div>
                )}

                {/* Full Time divider — only shown when there are ET events */}
                {hasET && <PhaseDivider label="Full Time" />}

                {/* Extra Time first half */}
                {etFirstHalf.length > 0 && (
                    <>
                        <PhaseDivider label="Extra Time" highlight />
                        <div className="space-y-0 mb-2">
                            {etFirstHalf.map((ev, i) => (
                                <EventBubble key={`et1-${i}`} event={ev} homeTeamId={homeTeamId} />
                            ))}
                        </div>
                    </>
                )}

                {/* ET Half Time divider */}
                {etFirstHalf.length > 0 && etSecondHalf.length > 0 && (
                    <PhaseDivider label="ET Half Time" highlight />
                )}

                {/* Extra Time second half */}
                {etSecondHalf.length > 0 && (
                    <div className="space-y-0 mb-2">
                        {etSecondHalf.map((ev, i) => (
                            <EventBubble key={`et2-${i}`} event={ev} homeTeamId={homeTeamId} />
                        ))}
                    </div>
                )}
            </div>
        </div>
        <RefereeCard matchId={matchId} />
        <PenaltyShootout matchId={matchId} m={m} isLive={isLive} />
        <MatchInsightsCard matchId={matchId} m={m} compId={compId} showOdds={!isFinished && !isLive} />
        {!isFinished && !isLive && <MatchPreviewCard matchId={matchId} m={m} compId={compId} />}
        {!isFinished && !isLive && <TeamPreviewCard matchId={matchId} m={m} />}
        <MatchStories m={m} />
        </div>
    );
}

// ── Player event helpers ──────────────────────────────────────────────────────

function buildPlayerEventMap(events) {
    const map = {};
    const add = (id, ev) => {
        if (!id || id === '0') return;
        if (!map[id]) map[id] = [];
        map[id].push(ev);
    };
    for (const ev of events || []) {
        if (ev.type === 'substitution') {
            add(ev.player_id,  { ...ev, subRole: 'on' });   // player1 = coming ON
            add(ev.player2_id, { ...ev, subRole: 'off' });  // player2 = going OFF
        } else {
            add(ev.player_id, ev);
        }
    }
    return map;
}

function fmtMin(ev) {
    return ev.minute_extra > 0 ? `${ev.minute}+${ev.minute_extra}'` : `${ev.minute}'`;
}

function PlayerEventIcons({ playerId, eventMap }) {
    const evs = eventMap?.[playerId] || [];
    if (!evs.length) return null;
    return (
        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
            {evs.map((ev, i) => {
                if (ev.deleted) return null;  // VAR-overturned — don't show as if it counted
                const m = fmtMin(ev);
                if (ev.type === 'goal' || ev.type === 'own goal') return (
                    <span key={i} className="flex items-center gap-0.5">
                        <span className="text-[11px]">⚽</span>
                        {ev.type === 'own goal' && <span className="text-[8px] text-orange-400 font-bold">og</span>}
                        <span className="text-[9px] text-muted-foreground tabular-nums">{m}</span>
                    </span>
                );
                if (ev.type === 'penalty goal') return (
                    <span key={i} className="flex items-center gap-0.5">
                        <span className="text-[11px]">⚽</span>
                        <span className="text-[8px] text-green-400 font-bold">P</span>
                        <span className="text-[9px] text-muted-foreground tabular-nums">{m}</span>
                    </span>
                );
                if (ev.type === 'yellow card') return (
                    <span key={i} className="flex items-center gap-0.5">
                        <span className="inline-block w-2.5 h-3.5 bg-yellow-400 rounded-[1px] flex-shrink-0" />
                        <span className="text-[9px] text-muted-foreground tabular-nums">{m}</span>
                    </span>
                );
                if (ev.type === 'red card' || ev.type === 'yellow/red card') return (
                    <span key={i} className="flex items-center gap-0.5">
                        <span className="inline-block w-2.5 h-3.5 bg-red-500 rounded-[1px] flex-shrink-0" />
                        <span className="text-[9px] text-muted-foreground tabular-nums">{m}</span>
                    </span>
                );
                if (ev.type === 'substitution') return (
                    <span key={i} className={`flex items-center gap-0.5 ${ev.subRole === 'off' ? 'text-red-400' : 'text-green-400'}`}>
                        <span className="text-[10px] font-bold leading-none">{ev.subRole === 'off' ? '↓' : '↑'}</span>
                        <span className="text-[9px] text-muted-foreground tabular-nums">{m}</span>
                    </span>
                );
                return null;
            })}
        </div>
    );
}

function computePitchAtMinute(lineups, allEvents, sliderMinute) {
    if (!lineups?.home || !lineups?.away) return { lineups, subbedOff: { home: [], away: [] } };
    const subs = (allEvents || [])
        .filter(e => e.type === 'substitution' && e.minute <= sliderMinute)
        .sort((a, b) => (a.minute * 100 + (a.minute_extra || 0)) - (b.minute * 100 + (b.minute_extra || 0)));

    const homePlayers = (lineups.home.players || []).map(p => ({ ...p }));
    const awayPlayers = (lineups.away.players || []).map(p => ({ ...p }));
    const subbedOff = { home: [], away: [] };

    for (const sub of subs) {
        // player2_id = going OFF (starter), player_id = coming ON (bench)
        for (const [players, side] of [[homePlayers, 'home'], [awayPlayers, 'away']]) {
            const offIdx = players.findIndex(p => p.player_id === sub.player2_id && p.status === 'Start');
            if (offIdx === -1) continue;
            const offPlayer = players[offIdx];
            const onPlayer = (lineups[side]?.players || []).find(p => p.player_id === sub.player_id);
            subbedOff[side].push({ ...offPlayer, sub_minute: sub.minute, sub_minute_extra: sub.minute_extra || 0 });
            if (onPlayer) {
                players[offIdx] = { ...onPlayer, status: 'Start', position: offPlayer.position, formation_place: offPlayer.formation_place };
            } else {
                players.splice(offIdx, 1);
            }
        }
    }
    return {
        lineups: {
            ...lineups,
            home: { ...lineups.home, players: homePlayers },
            away: { ...lineups.away, players: awayPlayers },
        },
        subbedOff,
    };
}

// ── Landscape pitch ───────────────────────────────────────────────────────────

function groupByFormation(formationStr, players) {
    const starters = [...(players || [])]
        .filter(p => p.status === 'Start')
        .sort((a, b) => (a.formation_place || 99) - (b.formation_place || 99));

    if (!starters.length) return [];

    // ── Best path: slice directly by formation_place + formation string ─────────
    // e.g. "3-4-2-1" → sizes [1,3,4,2,1] → exact row split, no position guessing.
    // Works correctly for 4-2-3-1, 3-4-2-1, 3-5-2, etc.
    if (formationStr) {
        const parts = formationStr.split('-').map(Number).filter(n => n > 0);
        const sizes = [1, ...parts]; // GK row + one row per formation part
        const total = sizes.reduce((a, b) => a + b, 0);
        if (total === starters.length) {
            const rows = [];
            let idx = 0;
            for (const size of sizes) {
                rows.push(starters.slice(idx, idx + size));
                idx += size;
            }
            return rows.filter(r => r.length > 0);
        }
    }

    // ── Fallback: position-based grouping (covers missing formation data) ────────
    const pos = p => (p.position || '').toLowerCase();
    const gk  = starters.filter(p => pos(p).includes('goal') || pos(p) === 'g');
    const def = starters.filter(p => pos(p).includes('defend'));
    const mid = starters.filter(p => pos(p).includes('mid'));
    const fwd = starters.filter(p =>
        pos(p).includes('attack') || pos(p).includes('forward') ||
        pos(p).includes('striker'));

    if (gk.length && (def.length || mid.length || fwd.length)) {
        if (formationStr) {
            const parts = formationStr.split('-').map(Number).filter(n => n > 0);
            if (parts.length >= 4) {
                // Pool mid + fwd, then slice by middle and last formation parts
                const attacking = [...mid, ...fwd];
                const midRows = [];
                let idx = 0;
                for (let i = 1; i < parts.length - 1; i++) {
                    midRows.push(attacking.slice(idx, idx + parts[i]));
                    idx += parts[i];
                }
                const strikers = attacking.slice(idx);
                return [gk, def, ...midRows.filter(r => r.length), strikers]
                    .filter(r => r.length > 0);
            }
        }
        return [gk, def, mid, fwd].filter(r => r.length > 0);
    }

    return [starters];
}

// ── Player-stats utilities ────────────────────────────────────────────────────

/** Normalize a name for fuzzy matching: lowercase, strip diacritics, keep a-z 0-9 space.
 *  Handles Turkish (ı→i, ğ→g, ş→s), Scandinavian (ø→o, æ→ae), German (ß→ss), etc.
 *  These chars don't decompose under NFD so must be replaced before normalization. */
function normName(s) {
    return (s || '')
        .toLowerCase()
        // Characters that don't decompose under NFD — replace first
        .replace(/ı/g, 'i')   // Turkish dotless i
        .replace(/ğ/g, 'g')   // Turkish g-breve
        .replace(/ş/g, 's')   // Turkish s-cedilla
        .replace(/ø/g, 'o')   // Scandinavian
        .replace(/æ/g, 'ae')  // Scandinavian
        .replace(/ß/g, 'ss')  // German
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')   // strip all combining diacritics
        .replace(/[^a-z0-9 ]/g, '')
        .trim();
}

/**
 * Build two Maps (home, away) from the player-stats response, keyed by normalised name.
 * Multiple keys are stored per player to handle cross-provider naming differences:
 *
 *  • Full name          "gabriel magalhaes"
 *  • Reversed name      "magalhaes gabriel"  ← handles Brazilian/Spanish PA order
 *  • Last token         "magalhaes"           ← quick surname lookup
 *  • First token        "gabriel"             ← first-name-only fallback
 *
 * Keys are only added when they don't already exist (no collision overwrite).
 */
function buildStatsIndex(players, homeStatsTeamId) {
    const home = new Map(), away = new Map();
    for (const p of (players || [])) {
        const map = p.team_id === homeStatsTeamId ? home : away;
        const full = normName(p.player_name);
        const tokens = full.split(' ');
        const last = tokens[tokens.length - 1];
        const first = tokens[0];
        const reversed = tokens.length > 1 ? [...tokens].reverse().join(' ') : full;

        if (!map.has(full))     map.set(full, p);
        if (!map.has(reversed)) map.set(reversed, p);   // e.g. "magalhaes gabriel" for "Gabriel Magalhães"
        if (last  && !map.has(last))  map.set(last, p);
        if (first && !map.has(first)) map.set(first, p);
    }
    return { home, away };
}

/** Look up a PA player name in the correct index side. */
function lookupStats(index, playerName, isHome) {
    const map = isHome ? index?.home : index?.away;
    if (!map) return null;
    const full = normName(playerName);
    if (map.has(full)) return map.get(full);
    // Try reversed (PA "Magalhaes Gabriel" → reversed "gabriel magalhaes" which IS the StatsAPI key)
    const tokens = full.split(' ');
    if (tokens.length > 1) {
        const reversed = [...tokens].reverse().join(' ');
        if (map.has(reversed)) return map.get(reversed);
    }
    // Try last token (surname)
    const last = tokens[tokens.length - 1];
    if (last && map.has(last)) return map.get(last);
    // Try first token
    const first = tokens[0];
    return first ? (map.get(first) ?? null) : null;
}

const ratingColor = (r) =>
    r == null ? null : r >= 8 ? '#10b981' : r >= 7 ? '#84cc16' : r >= 6 ? '#f59e0b' : '#ef4444';

// Session-level heatmap cache: key = `${matchId}:${player_id}`
const _heatmapCache = new Map();

// ── Player stats tooltip (rendered as a portal to escape overflow clipping) ───

function PlayerStatsTip({ stats, rect, matchId, paPlayerId, isWc }) {
    const [heatmapPoints, setHeatmapPoints] = useState(null);
    const [wcImgErr, setWcImgErr] = useState(false);
    const [badgeErr, setBadgeErr] = useState(false);

    useEffect(() => {
        setHeatmapPoints(null);
        setWcImgErr(false);
        setBadgeErr(false);
        if (!stats?.player_id || !matchId) return;
        const key = `${matchId}:${stats.player_id}`;
        if (_heatmapCache.has(key)) {
            const cached = _heatmapCache.get(key);
            if (cached.length > 0) setHeatmapPoints(cached);
            return;
        }
        axios.get(
            `${API_URL}/api/football/match/${matchId}/player-heatmap/${stats.player_id}`,
            { headers: authHeaders() }
        ).then(r => {
            const pts = r.data?.points || [];
            _heatmapCache.set(key, pts);
            if (r.data?.available && pts.length > 0) setHeatmapPoints(pts);
        }).catch(() => { _heatmapCache.set(key, []); });
    }, [stats?.player_id, matchId]);

    if (!stats || !rect) return null;

    const { rating, player_name, position, minutes_played,
            shooting = {}, passing = {}, defending = {}, goalkeeping = {}, duels = {}, general = {} } = stats;
    const col = ratingColor(rating);
    const hasHeatmap = heatmapPoints && heatmapPoints.length > 0;
    const tipW = 480;

    const cx = typeof window !== 'undefined'
        ? Math.min(Math.max(tipW / 2 + 8, rect.left + rect.width / 2), window.innerWidth - tipW / 2 - 8)
        : rect.left + rect.width / 2;

    const style = {
        position: 'fixed',
        left: cx,
        top: rect.top - 10,
        transform: 'translate(-50%, -100%)',
        zIndex: 9999,
        pointerEvents: 'none',
        width: tipW,
    };

    const R = ({ l, v, em }) => {
        if (v == null || v === 0 || v === '0/0') return null;
        return (
            <div className="flex justify-between items-center gap-2 py-[2px]">
                <span className="text-white/40 truncate text-[10px]">{l}</span>
                <span className={`font-bold tabular-nums flex-shrink-0 text-[11px] ${em ? 'text-emerald-400' : 'text-white/90'}`}>{v}</span>
            </div>
        );
    };

    const shots      = shooting.total_shots || 0;
    const onTgt      = shooting.shots_on_target ?? 0;
    const passes     = passing.total_passes || 0;
    const accP       = passing.accurate_passes ?? 0;
    const crosses    = passing.total_crosses || 0;
    const accCross   = passing.accurate_crosses ?? 0;
    const longBalls  = passing.total_long_balls || 0;
    const accLong    = passing.accurate_long_balls ?? 0;
    const duelTotal  = (duels.duel_won || 0) + (duels.duel_lost || 0);

    const wcSrc = paPlayerId && !wcImgErr ? wcPlayerUrl(paPlayerId) : null;

    return createPortal(
        <div style={{ ...style, background: '#070714', minHeight: 290 }}
            className="rounded-2xl shadow-2xl overflow-hidden border border-white/10 flex">

            {/* ── LEFT: WC portrait image ── */}
            {isWc && (
                <div className="relative flex-shrink-0 w-[36%] bg-gradient-to-b from-[#0d0d20] to-[#070714]">
                    {wcSrc ? (
                        <img
                            src={wcSrc}
                            alt={player_name}
                            className="absolute inset-0 w-full h-full object-cover object-top"
                            onError={() => setWcImgErr(true)}
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-5xl font-black text-white/10">{(player_name || '?')[0].toUpperCase()}</span>
                        </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
                    <div className="absolute inset-y-0 right-0 w-5 bg-gradient-to-r from-transparent to-[#070714] pointer-events-none" />
                    <div className="absolute bottom-0 inset-x-0 px-3 pb-2.5">
                        <p className="text-white font-bold text-[12px] leading-tight drop-shadow truncate">{player_name}</p>
                        <p className="text-white/50 text-[10px] mt-0.5">{position}</p>
                    </div>
                </div>
            )}

            {/* ── RIGHT: stats panel ── */}
            <div className="flex-1 min-w-0 flex flex-col p-3 gap-2">
                {/* Header: name/minutes + badge + rating */}
                <div className="flex items-center gap-2 pb-2 border-b border-white/8">
                    <div className="min-w-0 flex-1">
                        {!isWc && (
                            <p className="font-bold text-white text-[12px] leading-tight truncate">{player_name}</p>
                        )}
                        <p className="text-white/40 text-[10px]">{!isWc && position ? `${position} · ` : ''}{minutes_played}&apos;</p>
                    </div>
                    {stats._pa_team_id && !badgeErr && (
                        <img src={badgeUrl(stats._pa_team_id)} alt=""
                            className="w-5 h-5 object-contain opacity-80 flex-shrink-0"
                            onError={() => setBadgeErr(true)} />
                    )}
                    {rating != null && (
                        <span className="font-black text-white text-[12px] px-1.5 py-0.5 rounded-md flex-shrink-0"
                            style={{ background: col }}>{rating.toFixed(1)}</span>
                    )}
                </div>

                {/* Stats in two columns */}
                <div className="grid grid-cols-2 gap-x-5 gap-y-0">
                    {/* Left col: attack + creation */}
                    <div>
                        <R l="Goals"        v={shooting.goals}              em={shooting.goals > 0}/>
                        <R l="Assists"      v={passing.assists}             em={passing.assists > 0}/>
                        <R l="Shots"        v={shots > 0 ? `${onTgt}/${shots}` : null}/>
                        <R l="xG"           v={shooting.expected_goals > 0 ? shooting.expected_goals?.toFixed(2) : null}/>
                        <R l="xA"           v={shooting.expected_assists > 0 ? shooting.expected_assists?.toFixed(2) : null}/>
                        <R l="Big chances"  v={shooting.big_chances_created} em={shooting.big_chances_created > 0}/>
                        <R l="Key passes"   v={passing.key_passes}          em={passing.key_passes > 0}/>
                        <R l="Crosses"      v={crosses > 0 ? `${accCross}/${crosses}` : null}/>
                        <R l="Long balls"   v={longBalls > 0 ? `${accLong}/${longBalls}` : null}/>
                        <R l="Saves"        v={goalkeeping.saves}           em={goalkeeping.saves > 0}/>
                    </div>
                    {/* Right col: possession + defending */}
                    <div>
                        <R l="Touches"      v={general.touches}/>
                        <R l="Passes"       v={passes > 0 ? `${accP}/${passes}` : null}/>
                        <R l="Duels won"    v={duelTotal > 0 ? `${duels.duel_won}/${duelTotal}` : null}/>
                        <R l="Aerial won"   v={duels.aerial_won}/>
                        <R l="Dribbles"     v={duels.won_contest}/>
                        <R l="Tackles"      v={defending.tackles}/>
                        <R l="Intercepts"   v={defending.interceptions}/>
                        <R l="Clearances"   v={defending.clearances}/>
                        <R l="Fouls"        v={general.fouls}/>
                        <R l="Fouled"       v={general.was_fouled}/>
                        <R l="Poss. lost"   v={general.possession_lost}/>
                        <R l="Disposs."     v={duels.dispossessed}/>
                    </div>
                </div>

                {/* Heatmap */}
                {hasHeatmap && (
                    <div className="border-t border-white/8 pt-2 mt-1">
                        <HeatmapPitch points={heatmapPoints} />
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

// ─────────────────────────────────────────────────────────────────────────────

function PitchPlayer({ player, onPlayerClick, eventMap, statsEntry, onPlayerHover, size = 'md', teamId }) {
    const evs = eventMap?.[player.player_id] || [];
    const hasGoal   = evs.some(e => !e.deleted && (e.type === 'goal' || e.type === 'penalty goal' || e.type === 'own goal'));
    const hasRed    = evs.some(e => !e.deleted && (e.type === 'red card' || e.type === 'yellow/red card'));
    const hasYellow = !hasRed && evs.some(e => !e.deleted && e.type === 'yellow card');
    const isSubOn   = evs.some(e => e.type === 'substitution' && e.subRole === 'on');
    const goalCount = evs.filter(e => !e.deleted && (e.type === 'goal' || e.type === 'penalty goal' || e.type === 'own goal')).length;
    const rating    = statsEntry?.rating ?? null;
    const rCol      = ratingColor(rating);
    const isXL      = size === 'xl';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onPlayerClick?.({ player_id: player.player_id, player: player.name, statsapiId: statsEntry?.player_id, statsEntry, teamId })}
            onKeyDown={e => e.key === 'Enter' && onPlayerClick?.({ player_id: player.player_id, player: player.name, statsapiId: statsEntry?.player_id, statsEntry, teamId })}
            onMouseEnter={statsEntry ? (e) => onPlayerHover?.(statsEntry, e.currentTarget.getBoundingClientRect(), player.player_id) : undefined}
            onMouseLeave={statsEntry ? () => onPlayerHover?.(null, null) : undefined}
            className="flex flex-col items-center gap-0.5 w-full px-0.5 cursor-pointer group"
        >
            <div className="relative">
                {/* Glow ring when stats available — signals the circle is clickable for heatmap */}
                {statsEntry && (
                    <span className={`absolute inset-0 rounded-full ring-2 ring-offset-1 ring-offset-transparent
                        ring-emerald-400/60 group-hover:ring-emerald-400 transition-all pointer-events-none
                        ${isXL ? '-inset-1' : '-inset-0.5'}`} />
                )}
                <PlayerCircle playerId={player.player_id} playerName={player.name} shirt={player.shirt} size={size} teamId={teamId} />
                {hasRed && (
                    <span className={`absolute -top-0.5 -right-0.5 rounded-[2px] border border-black/40 shadow bg-red-500
                        ${isXL ? 'w-5 h-6' : 'w-3 h-4'}`} />
                )}
                {hasYellow && (
                    <span className={`absolute -top-0.5 -right-0.5 rounded-[2px] border border-black/40 shadow bg-yellow-400
                        ${isXL ? 'w-5 h-6' : 'w-3 h-4'}`} />
                )}
                {isSubOn && (
                    <span className={`absolute -bottom-0.5 -left-0.5 rounded-full bg-green-500 border border-black/40 flex items-center justify-center shadow
                        ${isXL ? 'w-6 h-6' : 'w-3.5 h-3.5'}`}>
                        <span className={`font-black text-white leading-none ${isXL ? 'text-xs' : 'text-[7px]'}`}>↑</span>
                    </span>
                )}
                {/* Rating badge */}
                {rCol && (
                    <span className={`absolute left-1/2 -translate-x-1/2 font-black
                        px-1 rounded tabular-nums leading-snug text-white whitespace-nowrap
                        ${isXL ? '-bottom-4 text-xs' : '-bottom-2.5 text-[8px]'}`}
                        style={{ background: rCol }}>
                        {rating.toFixed(1)}
                    </span>
                )}
            </div>
            <span className={`text-white/90 text-center leading-tight w-full truncate font-semibold drop-shadow mt-1.5
                ${isXL ? 'text-sm' : 'text-[10px]'}`}>
                {player.name.split(' ').pop()}
            </span>
            {hasGoal && (
                <span className={`leading-none -mt-0.5 ${isXL ? 'text-base' : 'text-[9px]'}`}>
                    {'⚽'.repeat(goalCount)}
                </span>
            )}
        </div>
    );
}

function PitchColumn({ players, onPlayerClick, eventMap, statsLookup, onPlayerHover, size = 'md', teamId }) {
    return (
        <div className="flex-1 flex flex-col items-center justify-around h-full py-3 gap-1">
            {(players || []).map((p, i) => (
                <PitchPlayer key={p.player_id || i} player={p} onPlayerClick={onPlayerClick}
                    eventMap={eventMap} statsEntry={statsLookup?.(p.name)}
                    onPlayerHover={onPlayerHover} size={size} teamId={teamId}/>
            ))}
        </div>
    );
}

function PitchRow({ players, onPlayerClick, eventMap, statsLookup, onPlayerHover, teamId }) {
    return (
        <div className="flex-1 flex flex-row items-center justify-around w-full px-3">
            {(players || []).map((p, i) => (
                <PitchPlayer key={p.player_id || i} player={p} onPlayerClick={onPlayerClick}
                    eventMap={eventMap} statsEntry={statsLookup?.(p.name)}
                    onPlayerHover={onPlayerHover} teamId={teamId}/>
            ))}
        </div>
    );
}

function PortraitPitch({ lineups, onPlayerClick, eventMap, homeStatsLookup, awayStatsLookup, onPlayerHover }) {
    const { home, away } = lineups;
    const homeRows = groupByFormation(home?.formation, home?.players);
    const awayRows = groupByFormation(away?.formation, away?.players);

    const maxRows = Math.max(homeRows.length, awayRows.length);
    // Standard ratio (68/105) gives ~69px/row for 4-row formations — enough for 44px circle + name.
    // 5-row formations (e.g. 4-2-3-1) need ~55px/row at that ratio, which clips the GK.
    // Use a taller ratio so each row gets ~79px (enough for circle + gap + name text).
    const pitchH = maxRows > 4 ? 150 : 105;

    return (
        // Portrait pitch: aspect ratio adapts to formation depth so edge rows aren't clipped
        <div className="relative rounded-2xl overflow-hidden w-full" style={{ aspectRatio: `68 / ${pitchH}` }}>
            {/* Grass stripes — horizontal bands */}
            <div className="absolute inset-0 flex flex-col">
                {[...Array(12)].map((_, i) => (
                    <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-emerald-700' : 'bg-emerald-600'}`} />
                ))}
            </div>

            {/* Pitch markings */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-3 border border-white/20 rounded-sm" />
                <div className="absolute top-1/2 left-3 right-3 h-px bg-white/20" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full border border-white/20" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white/30" />
                <div className="absolute top-3 left-1/2 -translate-x-1/2 border border-white/15" style={{ width: '55%', height: '12%', borderTopWidth: 0 }} />
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 border border-white/15" style={{ width: '55%', height: '12%', borderBottomWidth: 0 }} />
                <div className="absolute top-3 left-1/2 -translate-x-1/2 border border-white/10" style={{ width: '28%', height: '5%', borderTopWidth: 0 }} />
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 border border-white/10" style={{ width: '28%', height: '5%', borderBottomWidth: 0 }} />
            </div>

            {/* Players */}
            <div className="absolute inset-0 flex flex-col">
                {/* Home — top half, GK at top, forwards toward centre */}
                <div className="flex-1 flex flex-col">
                    {homeRows.map((row, i) => (
                        <PitchRow key={i} players={row} onPlayerClick={onPlayerClick}
                            eventMap={eventMap} statsLookup={homeStatsLookup} onPlayerHover={onPlayerHover}
                            teamId={home?.team_id}/>
                    ))}
                </div>
                {/* Away — bottom half, GK at bottom, forwards toward centre */}
                {/* Rows are reversed horizontally so away RB mirrors home RB (opposite sides of pitch) */}
                <div className="flex-1 flex flex-col-reverse">
                    {awayRows.map((row, i) => (
                        <PitchRow key={i} players={[...row].reverse()} onPlayerClick={onPlayerClick}
                            eventMap={eventMap} statsLookup={awayStatsLookup} onPlayerHover={onPlayerHover}
                            teamId={away?.team_id}/>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Lineup sidebar (list beside pitch) ───────────────────────────────────────

function LineupSidebar({ team, align = 'left', fullWidth = false, onPlayerClick, eventMap, statsLookup, onPlayerHover, is4K = false }) {
    const rows = groupByFormation(team?.formation, team?.players);
    const subs = [...(team?.players || [])]
        .filter(p => p.status === 'Sub')
        .sort((a, b) => (parseInt(a.shirt) || 99) - (parseInt(b.shirt) || 99));
    const isRight = align === 'right';
    // A team sheet always reads GK→DEF→MID→FWD top-to-bottom, for both teams.
    // (We previously reversed the away rows to mirror the pitch direction, but
    // that put forwards on top and the keeper last, which reads wrong.)
    const displayRows = rows;

    const headerPy      = is4K ? 'py-5'    : 'py-4';
    const teamNameSize  = is4K ? 'text-xl'  : 'text-base';
    const formationSize = is4K ? 'text-base' : 'text-sm';
    const shirtWidth    = is4K ? 'w-8'      : 'w-5';
    const shirtSize     = is4K ? 'text-sm'  : 'text-[11px]';
    const playerSize    = is4K ? 'text-sm'  : 'text-xs';
    const rowPy         = is4K ? 'py-2.5'  : 'py-1.5';
    const subsLabelSize = is4K ? 'text-sm'  : 'text-[11px]';
    const subsPlayerSize= is4K ? 'text-sm'  : 'text-[11px]';
    const badgeSize     = is4K ? 'lg'       : 'lg';

    return (
        <div className={`flex flex-col bg-card border border-border/60 rounded-2xl overflow-hidden`}>
            {/* Team header */}
            <div className={`flex items-center gap-3 px-3 ${headerPy} border-b border-border/60 bg-muted/30 flex-shrink-0 ${isRight ? 'flex-row-reverse' : ''}`}>
                <TeamBadgeLight teamId={team?.team_id} teamName={team?.team_name} size={badgeSize} />
                <div className={`flex flex-col min-w-0 ${isRight ? 'items-end' : ''}`}>
                    <span className={`${teamNameSize} font-bold leading-tight ${isRight ? 'text-right' : ''}`}>{team?.team_name}</span>
                    {team?.formation && (
                        <span className={`${formationSize} text-muted-foreground font-semibold mt-0.5`}>{team.formation}</span>
                    )}
                </div>
            </div>

            {/* Content — flex-1 fills remaining sidebar height */}
            <div className="flex flex-col flex-1 px-2 py-3">

                {/* Starting XI */}
                <div className="flex flex-col">
                    {displayRows.map((row, ri) => (
                        <div key={ri} className={ri > 0 ? (is4K ? 'mt-4' : 'mt-2.5') : ''}>
                            {row.map(p => {
                                const se = statsLookup?.(p.name);
                                return (
                                <div key={p.player_id || p.name}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onPlayerClick?.({ player_id: p.player_id, player: p.name, statsapiId: se?.player_id, statsEntry: se, teamId: team?.team_id })}
                                    onKeyDown={e => e.key === 'Enter' && onPlayerClick?.({ player_id: p.player_id, player: p.name, statsapiId: se?.player_id, statsEntry: se, teamId: team?.team_id })}
                                    onMouseEnter={se ? (e) => onPlayerHover?.(se, e.currentTarget.getBoundingClientRect(), p.player_id) : undefined}
                                    onMouseLeave={se ? () => onPlayerHover?.(null, null) : undefined}
                                    className={`flex items-center gap-1.5 ${rowPy} rounded cursor-pointer hover:bg-muted/40 transition-colors ${isRight ? 'flex-row-reverse' : ''}`}>
                                    <span className={`${shirtSize} text-muted-foreground font-mono ${shirtWidth} text-right flex-shrink-0 leading-none`}>
                                        {p.shirt}
                                    </span>
                                    <span className={`${playerSize} font-medium leading-tight truncate flex-1 ${isRight ? 'text-right' : ''}`}>
                                        {p.name}
                                    </span>
                                    <PlayerEventIcons playerId={p.player_id} eventMap={eventMap} />
                                    {se && (
                                        <span className="text-emerald-400/70 flex-shrink-0 text-[10px] leading-none" title="Stats available">▤</span>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Subs */}
                {subs.length > 0 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <div className="border-t border-border/60 mt-3 mb-2 mx-1" />
                        <p className={`${subsLabelSize} text-muted-foreground font-semibold uppercase tracking-wide px-1 mb-1 ${isRight ? 'text-right' : ''}`}>
                            Subs
                        </p>
                        <div className="flex flex-col flex-1 justify-between pb-1">
                            {subs.map(p => {
                                const se = statsLookup?.(p.name);
                                return (
                                <div key={p.player_id || p.name}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onPlayerClick?.({ player_id: p.player_id, player: p.name, statsapiId: se?.player_id, statsEntry: se, teamId: team?.team_id })}
                                    onKeyDown={e => e.key === 'Enter' && onPlayerClick?.({ player_id: p.player_id, player: p.name, statsapiId: se?.player_id, statsEntry: se, teamId: team?.team_id })}
                                    onMouseEnter={se ? (e) => onPlayerHover?.(se, e.currentTarget.getBoundingClientRect(), p.player_id) : undefined}
                                    onMouseLeave={se ? () => onPlayerHover?.(null, null) : undefined}
                                    className={`flex items-center gap-1.5 opacity-60 rounded cursor-pointer hover:opacity-90 hover:bg-muted/40 transition-all ${isRight ? 'flex-row-reverse' : ''}`}>
                                    <span className={`${subsPlayerSize} text-muted-foreground font-mono ${shirtWidth} text-right flex-shrink-0 leading-none`}>
                                        {p.shirt}
                                    </span>
                                    <span className={`${subsPlayerSize} leading-tight truncate flex-1 ${isRight ? 'text-right' : ''}`}>
                                        {p.name}
                                    </span>
                                    <PlayerEventIcons playerId={p.player_id} eventMap={eventMap} />
                                </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function LandscapePitch({ lineups, onPlayerClick, eventMap, homeStatsLookup, awayStatsLookup, onPlayerHover, is4K = false }) {
    const { home, away } = lineups;
    const homeRows = groupByFormation(home?.formation, home?.players);
    const awayRows = groupByFormation(away?.formation, away?.players);
    const pSize = is4K ? 'xl' : 'md';
    // Larger pitch markings on 4K
    const circleSize = is4K ? 'w-52 h-52' : 'w-28 h-28';
    const dotSize    = is4K ? 'w-4 h-4'   : 'w-2 h-2';

    return (
        // aspect-ratio matches a real football pitch (105m × 68m ≈ 1.54:1 landscape)
        <div className="relative rounded-2xl overflow-hidden w-full" style={{ aspectRatio: '1.54 / 1' }}>
            {/* Grass stripes */}
            <div className="absolute inset-0 flex">
                {[...Array(12)].map((_, i) => (
                    <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-emerald-700' : 'bg-emerald-600'}`} />
                ))}
            </div>

            {/* Pitch markings */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-3 border border-white/20 rounded-sm" />
                <div className="absolute top-3 bottom-3 left-1/2 w-px bg-white/20" />
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${circleSize} rounded-full border border-white/20`} />
                <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${dotSize} rounded-full bg-white/30`} />
                {/* Penalty boxes */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 border border-white/15" style={{ height: '55%', width: '12%', borderLeftWidth: 0 }} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 border border-white/15" style={{ height: '55%', width: '12%', borderRightWidth: 0 }} />
                {/* 6-yard boxes */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 border border-white/10" style={{ height: '28%', width: '5%', borderLeftWidth: 0 }} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 border border-white/10" style={{ height: '28%', width: '5%', borderRightWidth: 0 }} />
            </div>

            {/* Players — home attacks left→right, away attacks right→left.
                Home columns are reversed so the RB (highest formation_place) sits at the
                top of the screen (north touchline), matching the portrait view where RB is
                on the right. Away columns stay in formation_place order (LB at top), which
                mirrors home correctly — each pair of opposing full-backs shares the same
                touchline (home RB ↔ away LB both at top). */}
            <div className="absolute inset-0 flex">
                <div className="flex-1 flex flex-row">
                    {homeRows.map((col, i) => <PitchColumn key={i} players={[...col].reverse()} onPlayerClick={onPlayerClick}
                        eventMap={eventMap} statsLookup={homeStatsLookup} onPlayerHover={onPlayerHover} size={pSize} teamId={home?.team_id}/>)}
                </div>
                <div className="flex-1 flex flex-row-reverse">
                    {awayRows.map((col, i) => <PitchColumn key={i} players={col} onPlayerClick={onPlayerClick}
                        eventMap={eventMap} statsLookup={awayStatsLookup} onPlayerHover={onPlayerHover} size={pSize} teamId={away?.team_id}/>)}
                </div>
            </div>
        </div>
    );
}

function ManagerPhoto({ managerId, managerName }) {
    const [err, setErr] = useState(false);
    if (!err && managerId) {
        return (
            <img
                src={playerUrl(managerId)}
                alt={managerName}
                className="w-12 h-12 rounded-full object-cover border-2 border-border flex-shrink-0"
                onError={() => setErr(true)}
            />
        );
    }
    return (
        <div className="w-12 h-12 rounded-full bg-muted border-2 border-border flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-muted-foreground">{(managerName || '?')[0]}</span>
        </div>
    );
}

function RefereeAndManagers({ lineups }) {
    const referee = lineups.referee;
    const homeManager = lineups.managers?.home;
    const awayManager = lineups.managers?.away;

    if (!referee && !homeManager && !awayManager) return null;

    const refFlag = getFlagUrl(referee?.nationality);
    const homeFlag = getFlagUrl(homeManager?.nationality);
    const awayFlag = getFlagUrl(awayManager?.nationality);

    return (
        <div className="rounded-2xl border bg-card divide-y divide-border mt-4">
            {/* Referee */}
            {referee?.name && (
                <div className="px-5 py-4 text-center">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Referee</p>
                    <div className="flex items-center justify-center gap-2">
                        {refFlag && (
                            <img src={refFlag} alt={referee.nationality}
                                className="w-5 h-4 object-cover rounded-sm flex-shrink-0"
                                onError={e => e.target.style.display = 'none'} />
                        )}
                        <span className="font-semibold text-foreground">{referee.name}</span>
                    </div>
                </div>
            )}

            {/* Managers */}
            {(homeManager || awayManager) && (
                <div className="px-5 py-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 text-center">Managers</p>
                    <div className="flex items-center justify-between gap-4">
                        {/* Home manager */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {homeManager ? (
                                <>
                                    <ManagerPhoto managerId={homeManager.id} managerName={homeManager.name} />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            {homeFlag && (
                                                <img src={homeFlag} alt={homeManager.nationality}
                                                    className="w-4 h-3 object-cover rounded-sm flex-shrink-0"
                                                    onError={e => e.target.style.display = 'none'} />
                                            )}
                                            <span className="font-semibold text-sm truncate">{homeManager.name}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{homeManager.role || 'Coach'}</p>
                                    </div>
                                </>
                            ) : <div className="flex-1" />}
                        </div>

                        <div className="w-px h-10 bg-border flex-shrink-0" />

                        {/* Away manager */}
                        <div className="flex items-center gap-3 flex-1 min-w-0 flex-row-reverse">
                            {awayManager ? (
                                <>
                                    <ManagerPhoto managerId={awayManager.id} managerName={awayManager.name} />
                                    <div className="min-w-0 text-right">
                                        <div className="flex items-center gap-1.5 justify-end">
                                            <span className="font-semibold text-sm truncate">{awayManager.name}</span>
                                            {awayFlag && (
                                                <img src={awayFlag} alt={awayManager.nationality}
                                                    className="w-4 h-3 object-cover rounded-sm flex-shrink-0"
                                                    onError={e => e.target.style.display = 'none'} />
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{awayManager.role || 'Coach'}</p>
                                    </div>
                                </>
                            ) : <div className="flex-1" />}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function LineupQuickButtons({ sliderMinute, maxMinute, onChange, isFinished }) {
    return (
        <div className="flex gap-2 justify-center">
            <button
                onClick={() => onChange(0)}
                className={`text-[11px] px-3 py-1.5 rounded-md font-semibold transition-colors ${
                    sliderMinute === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
            >
                Starting XI
            </button>
            {isFinished && (
                <button
                    onClick={() => onChange(maxMinute)}
                    className={`text-[11px] px-3 py-1.5 rounded-md font-semibold transition-colors ${
                        sliderMinute >= maxMinute ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                    }`}
                >
                    Full Time
                </button>
            )}
        </div>
    );
}

function LineupSliderBar({ sliderMinute, maxMinute, onChange }) {
    const label = sliderMinute === 0 ? 'KO'
        : sliderMinute === 45 ? 'HT'
        : sliderMinute >= maxMinute ? 'FT'
        : `${sliderMinute}'`;
    return (
        <div className="rounded-xl bg-card border border-border/60 px-4 py-3">
            <div className="flex items-center gap-3">
                <span className="text-[11px] font-semibold text-muted-foreground w-6 text-center shrink-0">KO</span>
                <div className="flex-1 relative flex items-center">
                    <input
                        type="range"
                        min={0}
                        max={maxMinute}
                        value={sliderMinute}
                        onChange={e => onChange(parseInt(e.target.value))}
                        style={{ accentColor: 'var(--primary, #6d28d9)' }}
                        className="w-full h-1.5 rounded-full cursor-pointer"
                    />
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-px h-3 bg-border/80 pointer-events-none"
                        style={{ left: `${(45 / maxMinute) * 100}%` }}
                    />
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground w-6 text-center shrink-0">FT</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full min-w-[36px] text-center tabular-nums shrink-0 ${
                    sliderMinute === 0 || sliderMinute >= maxMinute
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-primary/20 text-primary'
                }`}>{label}</span>
            </div>
        </div>
    );
}

function SubbedOffPlayer({ player, onPlayerClick, statsEntry, onPlayerHover, teamId }) {
    const m = player.sub_minute_extra > 0
        ? `${player.sub_minute}+${player.sub_minute_extra}'`
        : `${player.sub_minute}'`;
    const rc = statsEntry ? ratingColor(statsEntry.rating) : null;
    return (
        <button
            onClick={() => onPlayerClick?.({ player_id: player.player_id, player: player.name, statsapiId: statsEntry?.player_id, statsEntry, teamId })}
            onMouseEnter={statsEntry ? (e) => onPlayerHover?.(statsEntry, e.currentTarget.getBoundingClientRect(), player.player_id) : undefined}
            onMouseLeave={statsEntry ? () => onPlayerHover?.(null, null) : undefined}
            className="flex flex-col items-center gap-0.5 opacity-70 hover:opacity-100 transition-opacity"
        >
            <div className="relative">
                <PlayerCircle playerId={player.player_id} playerName={player.name} shirt={player.shirt} teamId={teamId} />
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 border border-black/40 flex items-center justify-center shadow">
                    <span className="text-[7px] font-black text-white leading-none">↓</span>
                </span>
            </div>
            <span className="text-[9px] text-white/70 leading-tight truncate max-w-[52px] text-center font-medium">
                {player.name.split(' ').pop()}
            </span>
            <span className="text-[9px] text-red-400 tabular-nums font-semibold">{m}</span>
            {rc && (
                <span className="text-[8px] font-black px-1 rounded tabular-nums text-white leading-snug"
                    style={{ background: rc }}>
                    {statsEntry.rating.toFixed(1)}
                </span>
            )}
        </button>
    );
}

function SubbedOffPanel({ subbedOff, lineups, onPlayerClick, homeStatsLookup, awayStatsLookup, onPlayerHover }) {
    const hasHome = subbedOff.home.length > 0;
    const hasAway = subbedOff.away.length > 0;
    if (!hasHome && !hasAway) return null;
    return (
        <div className="rounded-xl bg-card border border-border/60 px-4 py-3 space-y-3">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide text-center">Subbed off</p>
            <div className="flex gap-4">
                <div className="flex-1 space-y-1.5">
                    {hasHome && (
                        <>
                            <div className="flex items-center gap-1.5">
                                <TeamBadgeLight teamId={lineups?.home?.team_id} teamName={lineups?.home?.team_name} />
                                <span className="text-[10px] text-muted-foreground truncate">{lineups?.home?.team_name}</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {subbedOff.home.map(p => (
                                    <SubbedOffPlayer key={p.player_id} player={p} onPlayerClick={onPlayerClick}
                                        statsEntry={homeStatsLookup?.(p.name)} onPlayerHover={onPlayerHover}
                                        teamId={lineups?.home?.team_id}/>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="flex-1 space-y-1.5">
                    {hasAway && (
                        <>
                            <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-muted-foreground truncate text-right">{lineups?.away?.team_name}</span>
                                <TeamBadgeLight teamId={lineups?.away?.team_id} teamName={lineups?.away?.team_name} />
                            </div>
                            <div className="flex flex-wrap gap-3 justify-end">
                                {subbedOff.away.map(p => (
                                    <SubbedOffPlayer key={p.player_id} player={p} onPlayerClick={onPlayerClick}
                                        statsEntry={awayStatsLookup?.(p.name)} onPlayerHover={onPlayerHover}
                                        teamId={lineups?.away?.team_id}/>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// True when the primary input is touch (phones/tablets) — evaluated once at module load
const IS_TOUCH_DEVICE = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

function LineupView({ lineups, events, maxMinute = 90, onPlayerClick, isFinished,
                      homeStatsLookup, awayStatsLookup, is4K = false, matchId,
                      homeTeamId, awayTeamId }) {
    // Patch lineups with match-level team IDs as fallback.
    // The lineUpsEnhanced API sometimes returns an empty @teamID; the match
    // document always has the correct PA team ID so WC-image detection works.
    const patchedLineups = useMemo(() => ({
        ...lineups,
        home: lineups?.home
            ? { ...lineups.home, team_id: lineups.home.team_id || homeTeamId || '' }
            : lineups?.home,
        away: lineups?.away
            ? { ...lineups.away, team_id: lineups.away.team_id || awayTeamId || '' }
            : lineups?.away,
    }), [lineups, homeTeamId, awayTeamId]);

    const { home, away } = patchedLineups;
    const hasSubs = (events || []).some(e => e.type === 'substitution');
    const [sliderMinute, setSliderMinute] = useState(maxMinute);
    // Tooltip state: { stats, rect } | null — suppressed on touch devices
    const [hoveredPlayer, setHoveredPlayer] = useState(null);

    const handlePlayerHover = useCallback((stats, rect, paPlayerId) => {
        // On touch devices the modal handles everything; skip the floating tooltip
        if (IS_TOUCH_DEVICE) return;
        setHoveredPlayer(stats && rect ? { stats, rect, paPlayerId } : null);
    }, []);

    // Only show events that have happened up to the current slider minute
    const filteredEvents = useMemo(
        () => (events || []).filter(e => e.minute <= sliderMinute),
        [events, sliderMinute]
    );
    const eventMap = useMemo(() => buildPlayerEventMap(filteredEvents), [filteredEvents]);

    const { lineups: computedLineups, subbedOff } = useMemo(
        () => computePitchAtMinute(patchedLineups, events, sliderMinute),
        [patchedLineups, events, sliderMinute]
    );

    const showSubsStrip = subbedOff.home.length > 0 || subbedOff.away.length > 0;

    const quickButtons = hasSubs ? (
        <LineupQuickButtons sliderMinute={sliderMinute} maxMinute={maxMinute} onChange={setSliderMinute} isFinished={isFinished} />
    ) : null;

    const sliderBar = hasSubs ? (
        <LineupSliderBar sliderMinute={sliderMinute} maxMinute={maxMinute} onChange={setSliderMinute} />
    ) : null;

    const subbedOffPanel = showSubsStrip ? (
        <SubbedOffPanel subbedOff={subbedOff} lineups={patchedLineups} onPlayerClick={onPlayerClick}
            homeStatsLookup={homeStatsLookup} awayStatsLookup={awayStatsLookup}
            onPlayerHover={handlePlayerHover}/>
    ) : null;

    return (
        <>
            {/* Floating tooltip portal */}
            {hoveredPlayer && (
                <PlayerStatsTip stats={hoveredPlayer.stats} rect={hoveredPlayer.rect} matchId={matchId} paPlayerId={hoveredPlayer.paPlayerId}
                    isWc={isWcTeam(homeTeamId) || isWcTeam(awayTeamId)}/>
            )}

            {/* Mobile: portrait pitch on top, two-column list below */}
            <div className="md:hidden space-y-3">
                {quickButtons}
                <PortraitPitch lineups={computedLineups} eventMap={eventMap} onPlayerClick={onPlayerClick}
                    homeStatsLookup={homeStatsLookup} awayStatsLookup={awayStatsLookup}
                    onPlayerHover={handlePlayerHover}/>
                {sliderBar}
                {subbedOffPanel}
                <div className="grid grid-cols-2 gap-2">
                    <LineupSidebar team={home} align="left" fullWidth eventMap={eventMap}
                        onPlayerClick={onPlayerClick} statsLookup={homeStatsLookup}
                        onPlayerHover={handlePlayerHover}/>
                    <LineupSidebar team={away} align="left" fullWidth eventMap={eventMap}
                        onPlayerClick={onPlayerClick} statsLookup={awayStatsLookup}
                        onPlayerHover={handlePlayerHover}/>
                </div>
                <RefereeAndManagers lineups={lineups} />
            </div>
            {/* Desktop: pitch in the centre */}
            <div className="hidden md:flex flex-col gap-4 w-full">
                <div className="grid gap-2 w-full" style={{
                    gridTemplateColumns: is4K
                        ? 'minmax(260px, 22%) 1fr minmax(260px, 22%)'
                        : 'minmax(190px, 21%) 1fr minmax(190px, 21%)'
                }}>
                    <LineupSidebar team={home} align="left" eventMap={eventMap}
                        onPlayerClick={onPlayerClick} statsLookup={homeStatsLookup}
                        onPlayerHover={handlePlayerHover} is4K={is4K}/>
                    <div className="flex flex-col gap-3 min-w-0">
                        {quickButtons}
                        <LandscapePitch lineups={computedLineups} eventMap={eventMap} onPlayerClick={onPlayerClick}
                            homeStatsLookup={homeStatsLookup} awayStatsLookup={awayStatsLookup}
                            onPlayerHover={handlePlayerHover} is4K={is4K}/>
                        {sliderBar}
                        {subbedOffPanel}
                    </div>
                    <LineupSidebar team={away} align="right" eventMap={eventMap}
                        onPlayerClick={onPlayerClick} statsLookup={awayStatsLookup}
                        onPlayerHover={handlePlayerHover} is4K={is4K}/>
                </div>
                <RefereeAndManagers lineups={lineups} />
            </div>
        </>
    );
}

// ── Player heatmap modal ──────────────────────────────────────────────────────

function HeatmapPitch({ points }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const W = canvas.width, H = canvas.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, W, H);

        // Pitch background — alternating green stripes
        const stripes = 8;
        const sw = W / stripes;
        for (let i = 0; i < stripes; i++) {
            ctx.fillStyle = i % 2 === 0 ? '#166534' : '#15803d';
            ctx.fillRect(i * sw, 0, sw, H);
        }

        // Minimal padding so the pitch fills as much of the canvas as possible,
        // with just enough room for the DEF/ATT labels at the bottom.
        const PL = 14, PR = 14, PT = 10, PB = 22;
        const PW = W - PL - PR, PH = H - PT - PB;

        const drawMarkings = () => {
            ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1.5;
            ctx.strokeRect(PL, PT, PW, PH);
            ctx.beginPath(); ctx.moveTo(W/2, PT); ctx.lineTo(W/2, PT + PH); ctx.stroke();
            ctx.beginPath(); ctx.arc(W/2, PT + PH/2, PH * 0.19, 0, Math.PI*2); ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.beginPath(); ctx.arc(W/2, PT + PH/2, 3, 0, Math.PI*2); ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = 'rgba(255,255,255,0.28)';
            const pbW = PW*0.14, pbH = PH*0.58, pbY = PT + PH*0.21;
            ctx.strokeRect(PL, pbY, pbW, pbH);
            ctx.strokeRect(PL + PW - pbW, pbY, pbW, pbH);
            ctx.strokeStyle = 'rgba(255,255,255,0.18)';
            const syW = PW*0.055, syH = PH*0.28, syY = PT + PH*0.36;
            ctx.strokeRect(PL, syY, syW, syH);
            ctx.strokeRect(PL + PW - syW, syY, syW, syH);
            ctx.fillStyle = 'rgba(255,255,255,0.45)';
            ctx.font = '600 10px sans-serif';
            ctx.fillText('DEF', PL + 4, H - 4);
            ctx.fillText('ATT', PL + PW - 28, H - 4);
        };

        if (!points?.length) { drawMarkings(); return; }

        // Density layer — coordinates mapped into the PITCH area (not the full canvas),
        // then clipped to the pitch rectangle so no heat spills into the crowd.
        const dc = document.createElement('canvas');
        dc.width = W; dc.height = H;
        const dCtx = dc.getContext('2d');

        // Clip the density layer to the pitch boundary
        dCtx.save();
        dCtx.beginPath();
        dCtx.rect(PL, PT, PW, PH);
        dCtx.clip();

        const radius = Math.min(PW, PH) * 0.12;
        for (const pt of points) {
            // Map [0,100] → [PL, PL+PW] and [0,100] → [PT, PT+PH]
            const x = PL + (pt.x / 100) * PW;
            const y = PT + (pt.y / 100) * PH;
            const g = dCtx.createRadialGradient(x, y, 0, x, y, radius);
            g.addColorStop(0,   'rgba(255,255,255,0.28)');
            g.addColorStop(0.4, 'rgba(255,255,255,0.12)');
            g.addColorStop(1,   'rgba(255,255,255,0)');
            dCtx.fillStyle = g;
            dCtx.beginPath(); dCtx.arc(x, y, radius, 0, Math.PI*2); dCtx.fill();
        }
        dCtx.restore();

        // Colorize: map alpha intensity → green→yellow→orange→red
        const dd = dCtx.getImageData(0, 0, W, H);
        const cc = document.createElement('canvas');
        cc.width = W; cc.height = H;
        const cCtx = cc.getContext('2d');
        const ci = cCtx.createImageData(W, H);
        // stops: [t, r, g, b, a]
        const stops = [
            [0,    0,   0,   0,   0  ],
            [0.12, 0,   200, 50,  90 ],
            [0.35, 255, 230, 0,   155],
            [0.6,  255, 80,  0,   205],
            [1.0,  220, 0,   0,   235],
        ];
        for (let i = 0; i < dd.data.length; i += 4) {
            const t = Math.min((dd.data[i + 3] / 255) * 2.8, 1);
            if (t < 0.02) continue;
            let r = 0, g2 = 0, b = 0, a = 0;
            for (let j = 1; j < stops.length; j++) {
                if (t <= stops[j][0]) {
                    const s = stops[j-1], e = stops[j];
                    const f = (t - s[0]) / (e[0] - s[0]);
                    r  = Math.round(s[1] + (e[1]-s[1])*f);
                    g2 = Math.round(s[2] + (e[2]-s[2])*f);
                    b  = Math.round(s[3] + (e[3]-s[3])*f);
                    a  = Math.round(s[4] + (e[4]-s[4])*f);
                    break;
                }
            }
            // clamp to last stop when t===1
            if (a === 0 && t >= 1) { const e = stops[stops.length-1]; r=e[1]; g2=e[2]; b=e[3]; a=e[4]; }
            ci.data[i]=r; ci.data[i+1]=g2; ci.data[i+2]=b; ci.data[i+3]=a;
        }
        cCtx.putImageData(ci, 0, 0);
        ctx.drawImage(cc, 0, 0);
        drawMarkings();
    }, [points]);

    return <canvas ref={canvasRef} width={560} height={340} className="w-full rounded-xl" />;
}

function PlayerStatsPanel({ stats, hideHeader = false }) {
    const { rating, player_name, position, minutes_played,
            shooting = {}, passing = {}, defending = {}, goalkeeping = {}, duels = {}, general = {} } = stats;
    const col = ratingColor(rating);

    const shots     = shooting.total_shots || 0;
    const onTgt     = shooting.shots_on_target ?? 0;
    const passes    = passing.total_passes || 0;
    const accP      = passing.accurate_passes ?? 0;
    const crosses   = passing.total_crosses || 0;
    const accCross  = passing.accurate_crosses ?? 0;
    const longBalls = passing.total_long_balls || 0;
    const accLong   = passing.accurate_long_balls ?? 0;
    const duelTotal = (duels.duel_won || 0) + (duels.duel_lost || 0);

    const R = ({ l, v, em }) => {
        if (v == null || v === 0 || v === '0/0') return null;
        return (
            <div className="flex justify-between items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                <span className="text-slate-400 text-sm">{l}</span>
                <span className={`font-bold tabular-nums text-sm ${em ? 'text-emerald-400' : 'text-white'}`}>{v}</span>
            </div>
        );
    };

    return (
        <div className="space-y-3">
            {!hideHeader && (
            <div className="flex items-center gap-2">
                {rating != null && (
                    <span className="font-black text-white text-xl px-2.5 py-1 rounded-xl flex-shrink-0"
                        style={{ background: col }}>{rating.toFixed(1)}</span>
                )}
                <div className="min-w-0">
                    <p className="font-bold text-white leading-tight text-sm truncate">{player_name}</p>
                    <p className="text-[11px] text-slate-400">{position} · {minutes_played}&apos;</p>
                </div>
            </div>
            )}
            <div>
                <R l="Minutes"       v={minutes_played} />
                <R l="Touches"       v={general.touches} />
                <R l="Goals"         v={shooting.goals}              em={shooting.goals > 0} />
                <R l="Assists"       v={passing.assists}             em={passing.assists > 0} />
                <R l="Shots"         v={shots > 0 ? `${onTgt} / ${shots}` : null} />
                <R l="xG"            v={shooting.expected_goals > 0 ? shooting.expected_goals?.toFixed(2) : null} />
                <R l="xA"            v={shooting.expected_assists > 0 ? shooting.expected_assists?.toFixed(2) : null} />
                <R l="Big chances"   v={shooting.big_chances_created} em={shooting.big_chances_created > 0} />
                <R l="Passes"        v={passes > 0 ? `${accP} / ${passes}` : null} />
                <R l="Key passes"    v={passing.key_passes}          em={passing.key_passes > 0} />
                <R l="Crosses"       v={crosses > 0 ? `${accCross} / ${crosses}` : null} />
                <R l="Long balls"    v={longBalls > 0 ? `${accLong} / ${longBalls}` : null} />
                <R l="Duels won"     v={duelTotal > 0 ? `${duels.duel_won} / ${duelTotal}` : null} />
                <R l="Aerial won"    v={duels.aerial_won} />
                <R l="Dribbles"      v={duels.won_contest} />
                <R l="Tackles"       v={defending.tackles} />
                <R l="Intercepts"    v={defending.interceptions} />
                <R l="Clearances"    v={defending.clearances} />
                <R l="Saves"         v={goalkeeping.saves}           em={goalkeeping.saves > 0} />
                <R l="Fouls"         v={general.fouls} />
                <R l="Fouled"        v={general.was_fouled} />
                <R l="Poss. lost"    v={general.possession_lost} />
                <R l="Disposs."      v={duels.dispossessed} />
            </div>
        </div>
    );
}

function PlayerHeatmapModal({ player, matchId, onClose, teamId }) {
    const [heatmapData, setHeatmapData] = useState(null);
    const [loading, setLoading]         = useState(true);

    useEffect(() => {
        if (!player?.statsapiId || !matchId) { setLoading(false); return; }
        axios.get(
            `${API_URL}/api/football/match/${matchId}/player-heatmap/${player.statsapiId}`,
            { headers: authHeaders() }
        )
            .then(r => setHeatmapData(r.data))
            .catch(() => setHeatmapData({ points: [], available: false }))
            .finally(() => setLoading(false));
    }, [player?.statsapiId, matchId]);

    const hasHeatmap = !loading && heatmapData?.available && heatmapData?.points?.length > 0;
    const stats = player?.statsEntry;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto bg-[#0d1828] border-white/10 text-white p-4">
                <DialogHeader className="sr-only">
                    <DialogTitle>{player?.player}</DialogTitle>
                </DialogHeader>

                {/* Player photo + name strip */}
                <div className="flex items-center gap-3 pb-3 border-b border-white/10 mt-1">
                    <PlayerCircle playerId={player?.player_id} playerName={player?.player}
                        shirt={null} size="lg" teamId={teamId ?? player?.teamId} />
                    <div className="min-w-0">
                        <p className="font-bold text-white text-base leading-tight truncate">{player?.player}</p>
                        {stats && (
                            <p className="text-[11px] text-slate-400">
                                {stats.position} · {stats.minutes_played}&apos; played
                            </p>
                        )}
                    </div>
                    {stats?.rating != null && (
                        <span className="ml-auto font-black text-white text-lg px-2.5 py-1 rounded-xl flex-shrink-0"
                            style={{ background: ratingColor(stats.rating) }}>
                            {stats.rating.toFixed(1)}
                        </span>
                    )}
                </div>

                {/* Body: portrait stack — heatmap first, then stats */}
                <div className="flex flex-col gap-4 mt-1">

                    {/* Heatmap */}
                    {loading ? (
                        <div className="rounded-xl bg-white/5 animate-pulse h-48 w-full" />
                    ) : hasHeatmap ? (
                        <div className="w-full">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
                                Heat Map
                            </p>
                            <HeatmapPitch points={heatmapData.points} />
                        </div>
                    ) : (
                        <p className="text-[11px] text-slate-500 text-center py-2">
                            Heatmap not available for this match.
                        </p>
                    )}

                    {/* Stats */}
                    {stats && (
                        <div className="w-full">
                            <PlayerStatsPanel stats={stats} hideHeader />
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ── Stats ─────────────────────────────────────────────────────────────────────

function StatBar({ label, home, away, isPct = false, isXg = false }) {
    const hVal  = parseFloat(home) ?? 0;
    const aVal  = parseFloat(away) ?? 0;
    const total = hVal + aVal;
    const hPct  = isPct ? hVal : (total > 0 ? (hVal / total) * 100 : 50);
    const fmt   = v => isPct ? `${v}%` : isXg ? v.toFixed(2) : v;
    const barH  = isXg ? 'h-3' : 'h-2';
    const barColH = isXg ? 'bg-amber-400' : 'bg-primary';
    const barColA = isXg ? 'bg-blue-400'  : 'bg-primary/20';

    return (
        <div className="space-y-1.5">
            <div className="flex justify-between items-center">
                <span className={`font-bold tabular-nums w-14 ${isXg ? 'text-amber-400 text-lg' : 'text-base text-foreground'}`}>
                    {fmt(hVal)}
                </span>
                <span className="text-xs text-muted-foreground font-medium text-center flex-1 mx-2">{label}</span>
                <span className={`font-bold tabular-nums w-14 text-right ${isXg ? 'text-blue-400 text-lg' : 'text-base text-foreground'}`}>
                    {fmt(aVal)}
                </span>
            </div>
            <div className={`${barH} flex rounded-full overflow-hidden bg-muted`}>
                <div className={`${barColH} rounded-full transition-all duration-500`} style={{ width: `${hPct}%` }} />
                <div className={`${barColA} flex-1 rounded-full`} />
            </div>
        </div>
    );
}

/** Parse "N/M" accurate pass strings → percentage or raw N */
function parseAccurate(val) {
    if (val == null) return null;
    if (typeof val === 'number') return val;
    const str = String(val);
    const m = str.match(/^(\d+)\/(\d+)$/);
    if (m) return Math.round((parseInt(m[1]) / parseInt(m[2])) * 100);
    return parseFloat(str) || null;
}

function StatSection({ title, children }) {
    // Hide the whole section if every row is null (live feed doesn't have this data yet)
    const rows = Array.isArray(children) ? children : [children];
    if (rows.every(c => c == null)) return null;
    return (
        <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1">{title}</p>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function EnrichedStatsView({ es, paStats, homeTeamName, awayTeamName, homeTeamId, awayTeamId }) {
    const ov  = es.overview  || {};
    const sh  = es.shots     || {};
    const atk = es.attack    || {};
    const ps  = es.passes    || {};
    const du  = es.duels     || {};
    const df  = es.defending || {};
    const gk  = es.goalkeeping || {};

    const row = (label, obj, opts = {}) => {
        if (obj?.home == null && obj?.away == null) return null;
        let h = obj.home, a = obj.away;
        if (opts.parse) { h = parseAccurate(h); a = parseAccurate(a); }
        return <StatBar key={label} label={label} home={h ?? 0} away={a ?? 0} isPct={!!opts.pct} isXg={!!opts.xg} />;
    };

    const teamsHeader = (
        <div className="flex justify-between items-center pb-3 border-b">
            <div className="flex items-center gap-2">
                <TeamBadgeLight teamId={homeTeamId} teamName={homeTeamName} size="md" />
                <span className="text-sm font-bold truncate max-w-[120px]">{homeTeamName}</span>
            </div>
            <span className="text-xs text-muted-foreground font-medium">vs</span>
            <div className="flex items-center gap-2 flex-row-reverse">
                <TeamBadgeLight teamId={awayTeamId} teamName={awayTeamName} size="md" />
                <span className="text-sm font-bold truncate max-w-[120px] text-right">{awayTeamName}</span>
            </div>
        </div>
    );

    return (
        <div className="rounded-2xl border bg-card p-5 space-y-6">
            {teamsHeader}

            {/* xG — prominent at the top */}
            {ov.xg?.home != null && (
                <StatSection title="Expected Goals">
                    {row('xG', ov.xg, { xg: true })}
                </StatSection>
            )}

            {/* Possession */}
            <StatSection title="Possession">
                {row('Possession %', ov.possession, { pct: true })}
            </StatSection>

            {/* Shots */}
            <StatSection title="Shots">
                {row('Total Shots',       sh.total)}
                {row('Shots on Target',   sh.on_target)}
                {row('Shots off Target',  sh.off_target)}
                {row('Inside Box',        sh.inside_box)}
                {row('Outside Box',       sh.outside_box)}
                {row('Blocked',           sh.blocked)}
                {row('Hit Woodwork',      sh.hit_woodwork)}
                {row('Big Chances',       ov.big_chances)}
                {row('Big Chances Missed',atk.big_chances_missed)}
                {row('Touches in Box',    atk.touches_in_penalty_area)}
            </StatSection>

            {/* Passing */}
            <StatSection title="Passing">
                {row('Accurate Passes',    ps.accurate_passes)}
                {row('Accurate Crosses',   ps.accurate_crosses)}
                {row('Accurate Long Balls',ps.accurate_long_balls)}
                {row('Final Third Entries',  ps.final_third_entries)}
            </StatSection>

            {/* Duels */}
            <StatSection title="Duels">
                {row('Aerial Duels Won %',  du.aerial_duels_pct,  { pct: true })}
                {row('Ground Duels Won %',  du.ground_duels_pct,  { pct: true })}
                {row('Dribbles Won %',      du.dribbles_pct,      { pct: true })}
            </StatSection>

            {/* Defending */}
            <StatSection title="Defending">
                {row('Tackles Won',     df.tackles_won_pct)}
                {row('Interceptions',   df.interceptions)}
                {row('Clearances',      df.clearances)}
                {row('Ball Recoveries', df.ball_recoveries)}
            </StatSection>

            {/* Goalkeeping */}
            <StatSection title="Goalkeeping">
                {row('Saves',            gk.saves)}
                {row('Goals Prevented',  gk.goals_prevented)}
                {row('High Claims',      gk.high_claims)}
            </StatSection>

            {/* Corners / Fouls / Cards — from PA if available, enriched otherwise */}
            <StatSection title="Set Pieces &amp; Discipline">
                {row('Corners',       ov.corner_kicks)}
                {row('Fouls',         ov.fouls)}
                {row('Yellow Cards',  ov.yellow_cards)}
                {row('Free Kicks',    ov.free_kicks)}
                {row('Offsides',      atk.offsides)}
            </StatSection>
        </div>
    );
}

function StatBarPA({ label, home, away }) {
    const hVal  = parseFloat(home) || 0;
    const aVal  = parseFloat(away) || 0;
    const total = hVal + aVal;
    const hPct  = total > 0 ? (hVal / total) * 100 : 50;
    const isPct = label.toLowerCase().includes('possession');

    return (
        <div className="space-y-1.5">
            <div className="flex justify-between items-center">
                <span className="text-base font-bold text-foreground tabular-nums w-12">
                    {isPct ? `${hVal}%` : hVal}
                </span>
                <span className="text-xs text-muted-foreground font-medium text-center flex-1 mx-2">{label}</span>
                <span className="text-base font-bold text-foreground tabular-nums w-12 text-right">
                    {isPct ? `${aVal}%` : aVal}
                </span>
            </div>
            <div className="h-2 flex rounded-full overflow-hidden bg-muted">
                <div className="bg-primary rounded-full transition-all duration-500" style={{ width: `${hPct}%` }} />
                <div className="bg-primary/20 flex-1 rounded-full" />
            </div>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MatchDetailPage() {
    const { matchId }  = useParams();
    const navigate     = useNavigate();
    const location     = useLocation();
    const matchHeader  = location.state?.match || null;

    const [events, setEvents]             = useState(null);
    const [lineups, setLineups]           = useState(null);
    const [playerStats, setPlayerStats]   = useState(null);
    const [stats, setStats]               = useState(null);
    const [enrichedStats, setEnrichedStats] = useState(null);
    const [profilePlayer, setProfilePlayer] = useState(null);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [lineupsLoading, setLineupsLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [tab, setTab]                   = useState('summary');
    const [showMatchView, setShowMatchView] = useState(false);
    const [liveStatus,    setLiveStatus]    = useState(null);

    // 4K kiosk detection — show all 3 panels simultaneously instead of tabs
    const [is4K, setIs4K] = useState(() => window.innerWidth >= 3000);
    useEffect(() => {
        const handler = () => setIs4K(window.innerWidth >= 3000);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Initial load
    useEffect(() => {
        setEventsLoading(true);
        axios.get(`${API_URL}/api/football/hub/match/${matchId}`, { headers: authHeaders() })
            .then(r  => setEvents(r.data))
            .catch(() => setEvents({ events: [] }))
            .finally(() => setEventsLoading(false));
    }, [matchId]);

    // Live poll — re-fetch match detail every 30 s so score + events stay current.
    // Never self-cancel based on is_result: PA sets that flag prematurely on some competitions
    // (e.g. UCL), which would stop us receiving subsequent goals mid-match.
    useEffect(() => {
        const interval = setInterval(() => {
            axios.get(`${API_URL}/api/football/hub/match/${matchId}`, { headers: authHeaders() })
                .then(r => { setEvents(r.data); })
                .catch(() => {});
        }, 30000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchId]);

    useEffect(() => {
        if ((tab === 'lineup' || is4K) && !lineups && !lineupsLoading) {
            setLineupsLoading(true);
            // Fetch lineups + player ratings in parallel; player-stats is optional
            Promise.all([
                axios.get(`${API_URL}/api/football/hub/match/${matchId}/lineups`, { headers: authHeaders() }),
                axios.get(`${API_URL}/api/football/match/${matchId}/player-stats`, { headers: authHeaders() })
                    .catch(() => ({ data: null })),
            ]).then(([lineupsRes, psRes]) => {
                setLineups(lineupsRes.data);
                if (psRes.data?.players) setPlayerStats(psRes.data);
            }).catch(() => setLineups({}))
              .finally(() => setLineupsLoading(false));
        }
        if ((tab === 'stats' || is4K) && !stats && !statsLoading) {
            setStatsLoading(true);
            Promise.all([
                axios.get(`${API_URL}/api/football/hub/match/${matchId}/stats`, { headers: authHeaders() })
                    .catch(() => ({ data: { stats: [] } })),
                axios.get(`${API_URL}/api/football/match/${matchId}/match-stats`, { headers: authHeaders() })
                    .catch(() => ({ data: null })),
            ]).then(([paRes, enrichedRes]) => {
                setStats(paRes.data);
                setEnrichedStats(enrichedRes.data);
            }).finally(() => setStatsLoading(false));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, is4K, matchId]);

    // A direct match URL has no navigation-state metadata. If the events/info
    // response is also missing team identity, use the enhanced-lineup feed as
    // an independent fallback instead of rendering question marks.
    useEffect(() => {
        if (!events || events.home_team_name || events.away_team_name || lineups || lineupsLoading) return;
        setLineupsLoading(true);
        axios.get(`${API_URL}/api/football/hub/match/${matchId}/lineups`, { headers: authHeaders() })
            .then(r => setLineups(r.data))
            .catch(() => setLineups({}))
            .finally(() => setLineupsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, matchId]);

    // Merge: start with navigation state (rich metadata from hub), overlay events API
    // data. For fields the events API returns as empty string, keep the nav-state value.
    // On refresh nav-state is null, so events API must carry everything (backend now
    // caches fixture metadata keyed by match_id for exactly this case).
    const m = (() => {
        if (!events && !matchHeader) return null;
        if (!events) return matchHeader;
        const base = { ...matchHeader, ...events };
        // Never overwrite a good name/id with an empty string
        const lineupFallback = {
            home_team_name: lineups?.home?.team_name,
            away_team_name: lineups?.away?.team_name,
            home_team_id: lineups?.home?.team_id,
            away_team_id: lineups?.away?.team_id,
        };
        const pick = (key) => events[key] || matchHeader?.[key] || lineupFallback[key] || '';
        return {
            ...base,
            home_team_name: pick('home_team_name'),
            away_team_name: pick('away_team_name'),
            home_team_id:   pick('home_team_id'),
            away_team_id:   pick('away_team_id'),
            competition_id: pick('competition_id'),
            kickoff_time:   pick('kickoff_time'),
        };
    })();
    // m.is_result comes from PA match/events (isResult:yes) — reliable for ALL competitions.
    // This is the primary finished signal. DB status and liveStatus are supplementary.
    const isResultFromPA = m?.is_result === true;

    // DB-based flags — used for deciding whether to start the live-status poll
    const isLiveDB     = !!(m?.status && m.status.toLowerCase().includes('live'));
    const isFinishedDB = isResultFromPA
        || !!(m?.status && (m.status.toLowerCase().includes('finish') || m.status.toLowerCase() === 'ft'));

    // Time-based: kickoff within the past 3 hours → always poll live-status regardless of DB state.
    // This guards against premature isResult caching poisoning the poll trigger.
    const kickoffRecentlyStarted = (() => {
        try {
            if (!m?.kickoff_time) return false;
            const ko = new Date(m.kickoff_time);
            const minsAgo = (Date.now() - ko.getTime()) / 60000;
            return minsAgo >= 0 && minsAgo < 180;
        } catch { return false; }
    })();

    // Display flags:
    // - isFinished: only true when NO in-progress signals exist (live_minute, live phases, explicit live)
    // - isLive:     live_minute from PA events OR liveStatus in-progress phase OR liveStatus.is_live
    //
    // PA matchMinutes is the most reliable signal — it's set during play (incl HT) for all comps,
    // even when match/info (used by live-status) has no coverage for the competition.
    const _FINISHED_PHASES_PAGE    = ['full_time', 'after_et', 'abandoned', 'postponed'];
    const _IN_PROGRESS_PHASES_PAGE = ['first_half', 'second_half', 'half_time', 'et_first_half', 'et_half_time', 'et_second_half', 'penalties'];
    const _lsKnownPage             = liveStatus && liveStatus.phase !== 'unknown';
    const _lsExplicitlyLivePage    = liveStatus && liveStatus.is_live === true;
    const _lsExplicitlyNotLivePage = liveStatus && liveStatus.is_live === false;
    const _hasLiveMinutePage       = m?.live_minute != null;
    const _lsInProgressPage        = _lsKnownPage && _IN_PROGRESS_PHASES_PAGE.includes(liveStatus.phase);
    // is_result from PA and a definitively-finished liveStatus phase both override live_minute still
    // being set in the DB (PA can return matchMinutes=131 and is_result=true simultaneously at PTFT).
    const _definitelyOverPage = (
        (isResultFromPA && !_lsExplicitlyLivePage && !_lsInProgressPage)
        || (_lsKnownPage && _FINISHED_PHASES_PAGE.includes(liveStatus.phase) && !_lsInProgressPage)
        || (_lsExplicitlyNotLivePage && isLiveDB && !_lsInProgressPage)
    );
    const isFinished = _definitelyOverPage;
    const isLive = !isFinished && (
        _hasLiveMinutePage || _lsInProgressPage || (_lsKnownPage ? liveStatus.is_live === true : isLiveDB)
    );

    // Poll live-status (match/info XML) every 30 s while match is in progress.
    // Also poll when kickoff was recent, in case premature isResult poisoned isLiveDB.
    useEffect(() => {
        if (!isLiveDB && !isFinishedDB && !kickoffRecentlyStarted) return;
        const poll = async () => {
            try {
                const r = await axios.get(
                    `${API_URL}/api/football/hub/match/${matchId}/live-status`,
                    { headers: authHeaders() }
                );
                setLiveStatus(r.data);
            } catch {}
        };
        poll();
        // Keep polling if DB says live OR kickoff was recent (may still be in progress)
        if (!isLiveDB && !kickoffRecentlyStarted) return;
        const interval = setInterval(poll, 30000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLiveDB, isFinishedDB, kickoffRecentlyStarted, matchId]);
    const homeTeamId = m?.home_team_id || '';
    const lineupsReady = lineups && (lineups.home?.players?.length || lineups.away?.players?.length);
    const statsReady   = stats?.stats?.length > 0;
    // Only use enriched stats when they contain actual data (not just a pending stub)
    const enrichedStatsReady = enrichedStats && !enrichedStats.stats_pending &&
        (enrichedStats.overview || enrichedStats.shots || enrichedStats.passes);

    // Build per-player stats lookup from theStatsAPI data
    const statsIndex = useMemo(
        () => buildStatsIndex(playerStats?.players, playerStats?.home_statsapi_team_id),
        [playerStats]
    );
    const homeStatsLookup = useCallback(
        (name) => {
            const entry = lookupStats(statsIndex, name, true);
            return entry ? { ...entry, _pa_team_id: m?.home_team_id } : null;
        },
        [statsIndex, m?.home_team_id]
    );
    const awayStatsLookup = useCallback(
        (name) => {
            const entry = lookupStats(statsIndex, name, false);
            return entry ? { ...entry, _pa_team_id: m?.away_team_id } : null;
        },
        [statsIndex, m?.away_team_id]
    );

    const eventsArr = events?.events || [];
    const maxMinute = eventsArr.length > 0
        ? Math.max(90, ...eventsArr.map(e => e.minute + (e.minute_extra || 0)))
        : 90;

    // If loaded via the kiosk route (/wc2026/match/...) always return to /wc2026
    const isKioskRoute = location.pathname.startsWith('/wc2026/');
    const handleBack = () => isKioskRoute ? navigate('/wc2026') : navigate(-1);

    // Redirect to /wc2026 after 5 min inactivity when in kiosk mode
    useKioskInactivity({ enabled: isKioskRoute });

    return (
        <div className="container-app py-4 space-y-4">
            <Button variant="ghost" size="sm" className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
                onClick={handleBack}>
                <ArrowLeft className="h-4 w-4" />
                Back
            </Button>

            <MatchHeader m={m} liveStatus={liveStatus} navigate={navigate} isKioskRoute={isKioskRoute} matchId={matchId} />

            {/* Match View button — always available */}
            <button
                onClick={() => setShowMatchView(true)}
                className="w-full flex items-center justify-center gap-2 rounded-xl font-semibold text-sm py-3 transition-all
                    bg-gradient-to-br from-[#0a0a1a] via-[#000028] to-[#0d0d1d]
                    border border-white/10 text-white hover:border-white/25 hover:brightness-110 shadow-lg"
            >
                <Tv2 className="h-4 w-4" />
                {isLive ? 'Live Match View' : isFinished ? 'Replay Match View' : 'Match View'}
            </button>

            {is4K ? (
                /* ── 4K layout: Summary | Lineup (×3) | Stats — pitch gets 60% of width ── */
                <div className="grid grid-cols-5 gap-6 mt-4">
                    {/* Column 1 — Summary */}
                    <div className="flex flex-col gap-4">
                        <h2 className="text-xl font-bold tracking-tight border-b border-border/50 pb-2">Summary</h2>
                        {eventsLoading ? (
                            <div className="space-y-3">
                                {[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
                            </div>
                        ) : (
                            <SummaryTab
                                events={events?.events}
                                homeTeamId={homeTeamId}
                                m={m}
                                isFinished={isFinished}
                                isLive={isLive}
                                matchId={matchId}
                                compId={m?.competition_id || '100'}
                            />
                        )}
                    </div>

                    {/* Columns 2–4 — Lineup (60% of screen) */}
                    <div className="col-span-3 flex flex-col gap-4">
                        <h2 className="text-xl font-bold tracking-tight border-b border-border/50 pb-2">Lineup</h2>
                        {lineupsLoading ? (
                            <Skeleton className="h-[400px] w-full rounded-2xl" />
                        ) : lineupsReady ? (
                            <LineupView lineups={lineups} events={eventsArr} maxMinute={maxMinute}
                                onPlayerClick={setProfilePlayer} isFinished={isFinished}
                                homeStatsLookup={homeStatsLookup} awayStatsLookup={awayStatsLookup}
                                matchId={matchId} is4K={true}
                                homeTeamId={m?.home_team_id} awayTeamId={m?.away_team_id}/>
                        ) : (
                            <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground text-sm">
                                Lineup not available yet.
                            </div>
                        )}
                    </div>

                    {/* Column 5 — Stats */}
                    <div className="flex flex-col gap-4">
                        <h2 className="text-xl font-bold tracking-tight border-b border-border/50 pb-2">Stats</h2>
                        {statsLoading ? (
                            <div className="space-y-4">
                                {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-10 w-full rounded-xl" />)}
                            </div>
                        ) : enrichedStatsReady ? (
                            <EnrichedStatsView
                                es={enrichedStats}
                                paStats={stats}
                                homeTeamName={m?.home_team_name}
                                awayTeamName={m?.away_team_name}
                                homeTeamId={m?.home_team_id}
                                awayTeamId={m?.away_team_id}
                            />
                        ) : statsReady ? (
                            <div className="rounded-2xl border bg-card p-5 space-y-1">
                                <div className="flex justify-between items-center pb-3 border-b mb-4">
                                    <div className="flex items-center gap-2">
                                        <TeamBadgeLight teamId={m?.home_team_id} teamName={m?.home_team_name} size="md" />
                                        <span className="text-sm font-bold truncate max-w-[130px]">{m?.home_team_name}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground font-medium">vs</span>
                                    <div className="flex items-center gap-2 flex-row-reverse">
                                        <TeamBadgeLight teamId={m?.away_team_id} teamName={m?.away_team_name} size="md" />
                                        <span className="text-sm font-bold truncate max-w-[130px] text-right">{m?.away_team_name}</span>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    {stats.stats.map(s => (
                                        <StatBarPA key={s.type} label={s.label} home={s.home} away={s.away} />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground text-sm">
                                Stats not available yet.
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* ── Standard tab view (all screens < 3000px) ── */
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="w-full grid grid-cols-3 h-11">
                        <TabsTrigger value="summary"  className="text-sm font-semibold">Summary</TabsTrigger>
                        <TabsTrigger value="lineup"   className="text-sm font-semibold">Lineup</TabsTrigger>
                        <TabsTrigger value="stats"    className="text-sm font-semibold">Stats</TabsTrigger>
                    </TabsList>

                    {/* ── Summary ── */}
                    <TabsContent value="summary" className="mt-4">
                        {eventsLoading ? (
                            <div className="space-y-3">
                                {[1,2,3,4].map(i => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
                            </div>
                        ) : (
                            <SummaryTab
                                events={events?.events}
                                homeTeamId={homeTeamId}
                                m={m}
                                isFinished={isFinished}
                                isLive={isLive}
                                matchId={matchId}
                                compId={m?.competition_id || '100'}
                            />
                        )}
                    </TabsContent>

                    {/* ── Lineup ── */}
                    <TabsContent value="lineup" className="mt-4">
                        {lineupsLoading ? (
                            <Skeleton className="h-[400px] w-full rounded-2xl" />
                        ) : lineupsReady ? (
                            <LineupView lineups={lineups} events={eventsArr} maxMinute={maxMinute}
                                onPlayerClick={setProfilePlayer} isFinished={isFinished}
                                homeStatsLookup={homeStatsLookup} awayStatsLookup={awayStatsLookup}
                                matchId={matchId}
                                homeTeamId={m?.home_team_id} awayTeamId={m?.away_team_id}/>
                        ) : tab === 'lineup' ? (
                            <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground text-sm">
                                Lineup not available yet.
                            </div>
                        ) : null}
                    </TabsContent>

                    {/* ── Stats ── */}
                    <TabsContent value="stats" className="mt-4">
                        {statsLoading ? (
                            <div className="space-y-4">
                                {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-10 w-full rounded-xl" />)}
                            </div>
                        ) : enrichedStatsReady ? (
                            <EnrichedStatsView
                                es={enrichedStats}
                                paStats={stats}
                                homeTeamName={m?.home_team_name}
                                awayTeamName={m?.away_team_name}
                                homeTeamId={m?.home_team_id}
                                awayTeamId={m?.away_team_id}
                            />
                        ) : statsReady ? (
                            <div className="rounded-2xl border bg-card p-5 space-y-1">
                                <div className="flex justify-between items-center pb-3 border-b mb-4">
                                    <div className="flex items-center gap-2">
                                        <TeamBadgeLight teamId={m?.home_team_id} teamName={m?.home_team_name} size="md" />
                                        <span className="text-sm font-bold truncate max-w-[130px]">{m?.home_team_name}</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground font-medium">vs</span>
                                    <div className="flex items-center gap-2 flex-row-reverse">
                                        <TeamBadgeLight teamId={m?.away_team_id} teamName={m?.away_team_name} size="md" />
                                        <span className="text-sm font-bold truncate max-w-[130px] text-right">{m?.away_team_name}</span>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    {stats.stats.map(s => (
                                        <StatBarPA key={s.type} label={s.label} home={s.home} away={s.away} />
                                    ))}
                                </div>
                            </div>
                        ) : tab === 'stats' ? (
                            <div className="rounded-2xl border bg-card p-10 text-center text-muted-foreground text-sm">
                                Stats not available yet.
                            </div>
                        ) : null}
                    </TabsContent>

                </Tabs>
            )}

            {profilePlayer && (
                <PlayerProfileModal
                    playerId={profilePlayer.player_id}
                    playerName={profilePlayer.player}
                    teamId={profilePlayer.teamId}
                    compId={m?.competition_id}
                    onClose={() => setProfilePlayer(null)}
                />
            )}

            {showMatchView && (
                <MatchViewModal
                    matchId={matchId}
                    matchMeta={m}
                    liveStatus={liveStatus}
                    onClose={() => setShowMatchView(false)}
                />
            )}
        </div>
    );
}
